import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  Activity, TrendingUp, TrendingDown, Minus,
  CheckCircle2, XCircle, AlertTriangle, Clock,
  Globe, BarChart2, Users, ChevronDown, ChevronUp,
  Target, Zap, RefreshCw, Info, Share2, Loader, X,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import MarketRegimeShareCard from '../components/MarketRegimeShareCard';
import RegimeDayShareCard from '../components/RegimeDayShareCard';
import SimulatorShareCard from '../components/SimulatorShareCard';
import { captureAndShare } from '../utils/shareImage';

const ACCENT = '#0ea5e9';

// Secondary analysis (Sectors, Scanner Signals, What Worked, Entry Window,
// Signal Reliability) is hidden for now — this page's first-level job is the
// Expected -> Actual -> Verdict story (Global/Gap/FII-DII -> Open Behavior ->
// Match/Conflict). These are a layer down and add noise until that core story
// is solid. Flip to true to bring them back.
const SHOW_SECONDARY_ANALYSIS = false;

const PERIODS = [
  { key: '1w', label: '1W' },
  { key: '1m', label: '1M' },
  { key: '3m', label: '3M' },
];

// ── helpers ──────────────────────────────────────────────────────────────────

function biasColor(bias) {
  if (bias === 'bull' || bias === 'buy') return '#16C784';
  if (bias === 'bear' || bias === 'sell') return '#ef4444';
  return '#9CA3AF';
}

function gapBias(gapType) {
  if (gapType.includes('up'))   return 'bull';
  if (gapType.includes('down')) return 'bear';
  return 'neutral';
}

function BiasIcon({ bias, size = 12 }) {
  const color = biasColor(bias);
  const s = { color, width: size, height: size, flexShrink: 0 };
  if (bias === 'bull' || bias === 'buy') return <TrendingUp style={s} />;
  if (bias === 'bear' || bias === 'sell') return <TrendingDown style={s} />;
  return <Minus style={s} />;
}

function MatchBadge({ match, conflict }) {
  if (conflict) return (
    <span className="flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full"
      style={{ background: '#f59e0b20', color: '#f59e0b' }}>
      <AlertTriangle style={{ width: 9, height: 9 }} /> Conflict
    </span>
  );
  if (match) return (
    <span className="flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full"
      style={{ background: '#16C78420', color: '#16C784' }}>
      <CheckCircle2 style={{ width: 9, height: 9 }} /> Followed
    </span>
  );
  return (
    <span className="flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full"
      style={{ background: '#ef444420', color: '#ef4444' }}>
      <XCircle style={{ width: 9, height: 9 }} /> Diverged
    </span>
  );
}

function fmtSignal(name) {
  return name.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
}

// ── Global story icon ─────────────────────────────────────────────────────────
function GlobalStory({ components }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  if (!components || !Object.keys(components).length) return null;

  const arrow = (dir) => dir === 'bull' ? '↑' : dir === 'bear' ? '↓' : '─';
  const color = (dir) => dir === 'bull' ? '#16C784' : dir === 'bear' ? '#ef4444' : '#9CA3AF';
  const fmt   = (avg) => avg != null ? `${avg > 0 ? '+' : ''}${avg}%` : '─';

  const regions = [
    { key: 'us',   label: 'US',   detail: 'S&P + Dow' },
    { key: 'asia', label: 'Asia', detail: 'Nikkei + Hang Seng' },
    { key: 'eu',   label: 'EU',   detail: 'DAX + FTSE' },
  ];

  return (
    <div className="relative inline-flex" ref={ref}>
      <button
        onClick={() => setOpen(v => !v)}
        className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-mono font-semibold"
        style={{ background: '#ffffff08', border: '1px solid #ffffff15', letterSpacing: '0.05em' }}
        title="Global market story"
      >
        {regions.map(({ key }) => (
          <span key={key} style={{ color: color(components[key]?.dir) }}>
            {arrow(components[key]?.dir)}
          </span>
        ))}
      </button>

      {open && (
        <div
          className="absolute top-6 left-0 z-20 rounded-xl shadow-xl"
          style={{ background: '#111827', border: '1px solid #ffffff18', width: 240, padding: '10px 12px' }}
        >
          <p style={{ fontSize: 8, fontWeight: 700, letterSpacing: '0.08em', color: '#6B7280', marginBottom: 6, textTransform: 'uppercase' }}>
            Global Market Story
          </p>
          {regions.map(({ key, label, detail }) => {
            const c = components[key] || {};
            return (
              <div key={key} style={{ padding: '5px 0', borderBottom: key !== 'eu' ? '1px solid #ffffff0a' : 'none' }}>
                <div className="flex items-center justify-between">
                  <div className="flex flex-col">
                    <span style={{ fontSize: 10, fontWeight: 600, color: '#E5E7EB' }}>{label}</span>
                    <span style={{ fontSize: 8, color: '#4B5563' }}>{detail}</span>
                  </div>
                  <div className="flex flex-col items-end" style={{ gap: 2 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: color(c.dir) }}>
                      {arrow(c.dir)} {fmt(c.avg)}
                    </span>
                    <span style={{ fontSize: 8, color: c.live ? '#f59e0b' : '#4B5563' }}>
                      {c.date || '─'}{c.live ? ' · live' : ''}
                    </span>
                  </div>
                </div>
                {c.indices?.length > 0 && (
                  <div className="flex flex-col" style={{ gap: 1, marginTop: 3 }}>
                    {c.indices.map(idx => (
                      <div key={idx.symbol} className="flex items-center justify-between">
                        <span style={{ fontSize: 8, color: '#6B7280' }}>{idx.name}</span>
                        <span style={{ fontSize: 8, fontWeight: 600, color: idx.pct >= 0 ? '#16C784' : '#ef4444' }}>
                          {idx.pct >= 0 ? '+' : ''}{idx.pct}%
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
          <p style={{ fontSize: 8, marginTop: 6, color: '#4B5563' }}>
            S&P 35% · Nikkei 25% · HSI 20% · Dow 10% · DAX+FTSE 10%
          </p>
        </div>
      )}
    </div>
  );
}

// ── Reusable story badge base ─────────────────────────────────────────────────
// align='left' → popover opens rightward; align='right' → opens leftward (for right-column tiles)
function StoryBadge({ trigger, children, align = 'left' }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  return (
    <div className="relative inline-flex" ref={ref}>
      <button
        onClick={() => setOpen(v => !v)}
        className="px-1 py-0.5 rounded text-[9px] font-mono font-semibold"
        style={{ background: '#ffffff08', border: '1px solid #ffffff15' }}
      >
        {trigger}
      </button>
      {open && (
        <div
          className="absolute top-6 z-20 rounded-xl shadow-xl"
          style={{
            background: '#111827',
            border: '1px solid #ffffff18',
            width: 176,
            padding: '10px 12px',
            ...(align === 'right' ? { right: 0 } : { left: 0 }),
          }}
        >
          {children}
        </div>
      )}
    </div>
  );
}

// ── Gap story ─────────────────────────────────────────────────────────────────
function GapStory({ day }) {
  if (!day?.gapType) return null;

  const gapDir = day.gapType.includes('up') ? 'bull' : day.gapType.includes('down') ? 'bear' : null;
  const held    = gapDir && gapDir === day.actual;
  const filled  = gapDir && gapDir !== day.actual && day.actual !== 'neutral';
  const color   = (d) => d === 'bull' ? '#16C784' : d === 'bear' ? '#ef4444' : '#9CA3AF';

  const gapRules = {
    strong_up:   'Strong gaps (>0.8%) reverse ~40% of the time — do NOT chase open, wait 15 min.',
    mild_up:     'Mild gaps hold well if global + FII are aligned. Watch open 15 min.',
    flat:        'Flat opens reduce edge. Wait for a directional breakout post 9:30.',
    mild_down:   'Watch for early reversal. DII buying often absorbs mild gap downs.',
    strong_down: 'High reversal risk. Reduce size to half. Use 9:20 candle as reference.',
  };

  const outcomeLabel = held ? 'Gap held ✓' : filled ? 'Gap filled ↩' : 'No clear outcome';
  const outcomeColor = held ? '#16C784'   : filled ? '#f59e0b'     : '#9CA3AF';

  const trigger = (
    <span style={{ color: color(gapDir) }}>
      {gapDir === 'bull' ? '↑' : gapDir === 'bear' ? '↓' : '─'}
      {' '}
      <span style={{ color: outcomeColor, fontSize: 9 }}>{held ? '✓' : filled ? '↩' : '?'}</span>
    </span>
  );

  return (
    <StoryBadge trigger={trigger} align="right">
      <p style={{ fontSize: 8, fontWeight: 700, letterSpacing: '0.08em', color: '#6B7280', marginBottom: 6, textTransform: 'uppercase' }}>Gap Story</p>
      {[
        { label: 'Size',       val: day.gapValue || '─',  col: color(gapDir) },
        { label: 'Day closed', val: day.actual === 'bull' ? '↑ Bull' : day.actual === 'bear' ? '↓ Bear' : '─', col: color(day.actual) },
        { label: 'Outcome',    val: outcomeLabel,          col: outcomeColor },
      ].map(({ label, val, col }) => (
        <div key={label} className="flex justify-between items-center" style={{ padding: '4px 0', borderBottom: '1px solid #ffffff0a' }}>
          <span style={{ fontSize: 9, color: '#9CA3AF' }}>{label}</span>
          <span style={{ fontSize: 10, fontWeight: 700, color: col }}>{val}</span>
        </div>
      ))}
      {gapRules[day.gapType] && (
        <p style={{ fontSize: 9, marginTop: 6, color: '#6B7280', lineHeight: 1.4 }}>{gapRules[day.gapType]}</p>
      )}
    </StoryBadge>
  );
}

// ── FII / DII flow story ──────────────────────────────────────────────────────
function FlowStory({ day, which }) {
  if (day?.fiiNet == null && day?.diiNet == null) return null;

  const fiiNet = day.fiiNet;
  const diiNet = day.diiNet;
  const color  = (n) => n > 0 ? '#16C784' : n < 0 ? '#ef4444' : '#9CA3AF';
  const arrow  = (n) => n > 0 ? '↑' : n < 0 ? '↓' : '─';
  const fmt    = (n) => n != null ? `${n > 0 ? '+' : ''}${Math.round(n).toLocaleString('en-IN')} Cr` : '─';

  const combined  = (fiiNet ?? 0) + (diiNet ?? 0);
  const aligned   = fiiNet != null && diiNet != null && Math.sign(fiiNet) === Math.sign(diiNet);
  const divergent = fiiNet != null && diiNet != null && Math.sign(fiiNet) !== Math.sign(diiNet);

  const relationship = aligned
    ? (fiiNet > 0 ? 'Both buying — reinforced bull flow' : 'Both selling — reinforced bear flow')
    : divergent
    ? (diiNet > 0 ? 'DII absorbing FII sell-off' : 'FII buying despite DII pressure')
    : '─';

  const relColor = aligned
    ? (fiiNet > 0 ? '#16C784' : '#ef4444')
    : '#f59e0b';

  const focusNet  = which === 'fii' ? fiiNet  : diiNet;
  const trigger   = <span style={{ color: color(focusNet) }}>{arrow(focusNet)} {fmt(focusNet)?.split(' ')[0]}</span>;

  return (
    <StoryBadge trigger={trigger} align={which === 'dii' ? 'right' : 'left'}>
      <p style={{ fontSize: 8, fontWeight: 700, letterSpacing: '0.08em', color: '#6B7280', marginBottom: 6, textTransform: 'uppercase' }}>Institutional Flow</p>
      {[
        { label: 'FII',          val: `${arrow(fiiNet)} ${fmt(fiiNet)}`,     col: color(fiiNet) },
        { label: 'DII',          val: `${arrow(diiNet)} ${fmt(diiNet)}`,     col: color(diiNet) },
        { label: 'Net combined', val: `${arrow(combined)} ${fmt(combined)}`, col: color(combined) },
      ].map(({ label, val, col }) => (
        <div key={label} className="flex justify-between items-center" style={{ padding: '4px 0', borderBottom: '1px solid #ffffff0a' }}>
          <span style={{ fontSize: 9, color: '#9CA3AF' }}>{label}</span>
          <span style={{ fontSize: 10, fontWeight: 700, color: col }}>{val}</span>
        </div>
      ))}
      <p style={{ fontSize: 9, marginTop: 6, fontWeight: 600, color: relColor }}>{relationship}</p>
    </StoryBadge>
  );
}

function StockRow({ n, isBull }) {
  const followed = n.followed;
  const pctColor = followed ? (isBull ? '#16C784' : '#ef4444') : (isBull ? '#ef4444' : '#16C784');
  return (
    <div className="flex items-center justify-between text-[10px] py-0.5">
      <div className="flex items-center gap-1.5 min-w-0 flex-1">
        <span className="font-semibold flex-shrink-0" style={{ color: '#ffffff' }}>{n.s}</span>
        <span className="truncate" style={{ color: '#9CA3AF' }}>{fmtSignal(n.signal)}</span>
        {n.strength === 'strong' && (
          <span className="text-[8px] px-1 rounded flex-shrink-0" style={{ background: '#f59e0b20', color: '#f59e0b' }}>
            S
          </span>
        )}
      </div>
      <div className="flex items-center gap-1.5 flex-shrink-0 ml-2">
        <span className="font-semibold tabular-nums" style={{ color: pctColor }}>
          {n.pct >= 0 ? '+' : ''}{n.pct}%
        </span>
        <span className="text-[10px] font-bold" style={{ color: followed ? '#16C784' : '#ef4444' }}>
          {followed ? '✓' : '✗'}
        </span>
      </div>
    </div>
  );
}

function InfoCard({ items }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        onClick={() => setOpen(v => !v)}
        style={{ display: 'flex', alignItems: 'center', padding: 2, color: open ? '#0ea5e9' : '#6B7280' }}
      >
        <Info style={{ width: 12, height: 12 }} />
      </button>

      {open && (
        <div
          style={{
            position: 'absolute', top: 20, left: 0, zIndex: 50,
            width: 280, borderRadius: 10, padding: '12px 14px',
            background: '#1a2236', border: '1px solid #2d3a50',
            boxShadow: '0 8px 32px rgba(0,0,0,0.45)',
          }}
        >
          {items.map((item, i) => (
            <div key={i} style={{ marginBottom: i < items.length - 1 ? 10 : 0 }}>
              <p style={{ fontSize: 10, fontWeight: 700, color: '#e2e8f0', marginBottom: 2 }}>{item.term}</p>
              <p style={{ fontSize: 10, color: '#94a3b8', lineHeight: 1.5 }}>{item.desc}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const MARKET_STRUCTURE_INFO = [
  { term: 'Breadth  e.g. 24% · 12/51', desc: 'How many Nifty stocks actually rose. 24% means only 12 of 51 closed positive — market was internally weak even if the index looked flat.' },
  { term: 'ATM Strike', desc: 'The at-the-money options strike — the one closest to where Nifty opened. CE = Call (betting up), PE = Put (betting down).' },
  { term: 'Call % / Put %', desc: 'How much the premium moved from open to close. Call +1.6% means call buyers made 1.6%. Put -17.8% means put buyers lost 17.8%.' },
  { term: 'Straddle %', desc: 'The combined Call + Put premium change. -8.4% means the total premium shrank — option sellers collected money; buyers lost.' },
];

const FO_PERFORMANCE_INFO = [
  { term: 'Straddle open → close', desc: 'Cost of buying both call and put at market open vs what it was worth at close. If it fell, sellers won.' },
  { term: 'Expected Move %', desc: 'Straddle price ÷ Nifty level × 100. The market was pricing this much % swing in Nifty for the day.' },
  { term: 'Actual Move %', desc: 'What Nifty actually moved. If this is less than Expected Move, the market stayed within range — sellers won.' },
  { term: 'CE % / PE %', desc: 'Individual performance of the call and put legs. Tells you which direction had actual intraday flow.' },
];

function ActionSummaryCard({ actionSummary, colors }) {
  if (!actionSummary) return null;
  const { combo, similar, followThrough, hitRate, modeLabel, modeColor, direction, date } = actionSummary;

  const dirColor = direction === 'bull' ? '#16C784' : direction === 'bear' ? '#ef4444' : '#9CA3AF';

  return (
    <div className="rounded-xl p-4 mb-4" style={{ background: `${modeColor}10`, border: `1px solid ${modeColor}35` }}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <p style={{ fontSize: 9, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6 }}>
            Most Recent Session · {date}
          </p>
          <p style={{ fontSize: 14, fontWeight: 700, color: modeColor, marginBottom: 6 }}>{modeLabel}</p>
          <p style={{ fontSize: 11, color: colors.secondaryText, lineHeight: 1.5 }}>
            <span style={{ color: dirColor, fontWeight: 600 }}>{direction?.toUpperCase()} setup</span>
            {' · '}{combo}
            {' · '}{similar >= 3
              ? `${followThrough}/${similar} similar sessions followed through`
              : 'Limited data — fewer than 3 similar sessions'}
          </p>
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <p style={{ fontSize: 32, fontWeight: 800, color: modeColor, lineHeight: 1 }}>
            {similar >= 3 ? `${hitRate}%` : '—'}
          </p>
          <p style={{ fontSize: 9, color: '#6B7280', marginTop: 2 }}>follow-through rate</p>
        </div>
      </div>
    </div>
  );
}

function VerdictRow({ verdict, colors }) {
  if (!verdict) return null;
  return (
    <div
      className="rounded-lg px-3.5 py-3"
      style={{ background: '#ffffff06', borderLeft: '2px solid #0ea5e9' }}
    >
      <p style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.06em', color: '#0ea5e9', textTransform: 'uppercase', marginBottom: 6 }}>
        Verdict
      </p>
      <p style={{ fontSize: 12, color: colors.primaryText, lineHeight: 1.65 }}>{verdict}</p>
    </div>
  );
}

function FOPerformanceSection({ foPerformance, colors }) {
  if (!foPerformance) return null;
  const {
    winner, winnerLabel, directionWinner,
    expectedMovePct, actualMovePct, moveRealized,
    straddleOpen, straddleClose, straddlePct,
    cePct, pePct, atmStrike,
  } = foPerformance;

  const winnerColor  = winner === 'sellers' ? '#16C784' : winner === 'buyers' ? '#ef4444' : '#9CA3AF';
  const straddleColor = straddlePct < 0 ? '#ef4444' : straddlePct > 0 ? '#16C784' : '#9CA3AF';
  const moveColor    = moveRealized ? '#ef4444' : '#16C784';

  return (
    <div
      className="rounded-lg px-3.5 py-3"
      style={{ background: `${winnerColor}0a`, border: `1px solid ${winnerColor}25` }}
    >
      <div className="flex items-center justify-between mb-2.5">
        <div className="flex items-center gap-1.5">
          <p style={{ fontSize: 8, fontWeight: 700, letterSpacing: '0.07em', color: '#9CA3AF', textTransform: 'uppercase' }}>
            FO Performance · ATM {atmStrike}
          </p>
          <InfoCard items={FO_PERFORMANCE_INFO} />
        </div>
        <span style={{ fontSize: 10, fontWeight: 700, color: winnerColor }}>{winnerLabel}</span>
      </div>

      {/* Straddle row */}
      <div className="flex gap-4 mb-2.5 flex-wrap">
        <div>
          <p style={{ fontSize: 8, color: '#6B7280', marginBottom: 1 }}>Straddle</p>
          <p style={{ fontSize: 11 }}>
            <span style={{ color: colors.primaryText }}>₹{straddleOpen} → ₹{straddleClose}</span>
            {'  '}
            <span style={{ fontWeight: 700, color: straddleColor }}>
              {straddlePct > 0 ? '+' : ''}{straddlePct}%
            </span>
          </p>
        </div>
        <div>
          <p style={{ fontSize: 8, color: '#6B7280', marginBottom: 1 }}>Expected Move</p>
          <p style={{ fontSize: 11, color: colors.primaryText }}>{expectedMovePct}%</p>
        </div>
        <div>
          <p style={{ fontSize: 8, color: '#6B7280', marginBottom: 1 }}>Actual Move</p>
          <p style={{ fontSize: 11, fontWeight: 600, color: moveColor }}>
            {actualMovePct}%
            <span style={{ fontSize: 9, fontWeight: 400, color: '#9CA3AF', marginLeft: 4 }}>
              {moveRealized ? '(realized)' : '(within range)'}
            </span>
          </p>
        </div>
      </div>

      {/* CE / PE */}
      <div className="flex items-center gap-3 flex-wrap">
        <span
          className="px-2 py-0.5 rounded"
          style={{ fontSize: 10, background: '#16C78415', color: '#16C784', fontWeight: 600 }}
        >
          CE {cePct > 0 ? '+' : ''}{cePct}%
        </span>
        <span
          className="px-2 py-0.5 rounded"
          style={{ fontSize: 10, background: '#ef444415', color: '#ef4444', fontWeight: 600 }}
        >
          PE {pePct > 0 ? '+' : ''}{pePct}%
        </span>
        <span style={{ fontSize: 10, color: '#9CA3AF' }}>{directionWinner}</span>
      </div>
    </div>
  );
}

function deriveWhatWorked({ foPerformance, match, conflict, expected, actual, breadth }) {
  const out = { bestTrade: null, worstTrade: null, instrumentFit: null, entryStrategy: null };

  if (foPerformance) {
    const { winner, expectedMovePct, actualMovePct, straddlePct, cePct, pePct } = foPerformance;

    if (winner === 'sellers') {
      out.bestTrade  = { label: 'Options Selling', tag: 'Straddle / Strangle Sell', color: '#16C784',
        reason: `Straddle decayed ${straddlePct}%. Market priced a ${expectedMovePct}% move but delivered only ${actualMovePct}%. Sellers collected the difference.` };
      out.worstTrade = { label: 'Directional Option Buy', color: '#ef4444',
        reason: 'Premium shrank both legs. CE and PE buyers were punished by time decay regardless of direction.' };
    } else if (winner === 'buyers') {
      if (match && expected === 'bull') {
        out.bestTrade  = { label: 'CE Buy', tag: 'Call Option Buy', color: '#16C784',
          reason: `Setup followed through bullish. Call gained ${cePct > 0 ? '+' : ''}${cePct}% intraday.` };
        out.worstTrade = { label: 'PE Buy', color: '#ef4444',
          reason: `Bearish bets lost. Put decayed ${pePct}% as market moved up.` };
      } else if (match && expected === 'bear') {
        out.bestTrade  = { label: 'PE Buy', tag: 'Put Option Buy', color: '#16C784',
          reason: `Setup followed through bearish. Put gained ${pePct > 0 ? '+' : ''}${pePct}% intraday.` };
        out.worstTrade = { label: 'CE Buy', color: '#ef4444',
          reason: `Bullish bets lost. Call decayed ${cePct}% as market moved down.` };
      } else {
        const winDir = actual === 'bull' ? 'CE' : 'PE';
        out.bestTrade  = { label: `${winDir} Buy (Contrarian)`, tag: 'Fade the setup', color: '#f59e0b',
          reason: `Setup said ${expected} but market went ${actual}. Those who faded the gap direction won.` };
        out.worstTrade = { label: 'Setup followers', color: '#ef4444',
          reason: 'Following the pre-market bias would have been on the wrong side of the reversal.' };
      }
    } else {
      out.bestTrade  = { label: 'Flat / Theta strategies', tag: 'Iron Condor / Short Strangle', color: '#9CA3AF',
        reason: `Low conviction day — straddle barely moved (${straddlePct}%). Range-bound strategies had the edge.` };
      out.worstTrade = { label: 'Directional trade', color: '#ef4444',
        reason: 'No clear direction. Directional bets on either side faced chop.' };
    }
  }

  if (breadth != null) {
    if (breadth < 30) {
      out.instrumentFit = { label: 'Index options / Nifty Futures',
        reason: `Only ${breadth}% of stocks participated. Market was narrow — index instruments gave cleaner signals than individual stocks.` };
    } else if (breadth > 65) {
      out.instrumentFit = { label: 'Stocks + Index both',
        reason: `${breadth}% breadth — broad participation. Stock picks and index trades both rewarding.` };
    } else {
      out.instrumentFit = { label: 'Index options preferred',
        reason: `Mixed breadth at ${breadth}%. Safer to trade the index than pick stocks.` };
    }
  }

  if (conflict) {
    out.entryStrategy = { label: 'Sit out or minimum size', color: '#6b7280',
      reason: 'Signals were contradictory — no edge to trade with conviction.' };
  } else if (!match) {
    out.entryStrategy = { label: 'Wait — fade at 9:35', color: '#f59e0b',
      reason: 'Setup diverged. The correct move was to watch the open, wait for 9:35, and fade the gap direction once the reversal was confirmed.' };
  } else {
    out.entryStrategy = { label: 'Trade with setup at open', color: '#16C784',
      reason: 'Setup followed through. Entering with the gap direction between 9:20–9:35 and holding would have worked.' };
  }

  return out;
}

function WhatWorkedSection({ day, colors }) {
  const { bestTrade, worstTrade, instrumentFit, entryStrategy } =
    deriveWhatWorked({
      foPerformance: day.foPerformance,
      match:    day.match,
      conflict: day.conflict,
      expected: day.expected,
      actual:   day.actual,
      breadth:  day.breadth,
    });

  if (!bestTrade && !entryStrategy) return null;

  const Row = ({ icon, label, tag, reason, color }) => (
    <div className="flex gap-2">
      <span style={{ fontSize: 13, lineHeight: 1, marginTop: 1 }}>{icon}</span>
      <div>
        <div className="flex items-center gap-2 flex-wrap" style={{ marginBottom: 2 }}>
          <span style={{ fontSize: 10, fontWeight: 700, color }}>{label}</span>
          {tag && (
            <span style={{ fontSize: 8, padding: '1px 6px', borderRadius: 20, background: `${color}18`, color, border: `1px solid ${color}30` }}>
              {tag}
            </span>
          )}
        </div>
        <p style={{ fontSize: 10, color: '#94a3b8', lineHeight: 1.5 }}>{reason}</p>
      </div>
    </div>
  );

  return (
    <div
      className="rounded-lg px-3 py-3"
      style={{ background: 'linear-gradient(135deg, #0ea5e910 0%, #0ea5e904 100%)', border: '1px solid #0ea5e920' }}
    >
      <p style={{ fontSize: 8, fontWeight: 700, letterSpacing: '0.07em', color: '#0ea5e9', textTransform: 'uppercase', marginBottom: 10 }}>
        What Worked This Day
      </p>

      <div className="flex flex-col gap-3">
        {bestTrade && (
          <Row icon="✅" label={bestTrade.label} tag={bestTrade.tag} reason={bestTrade.reason} color={bestTrade.color} />
        )}
        {worstTrade && (
          <Row icon="❌" label={worstTrade.label} reason={worstTrade.reason} color={worstTrade.color} />
        )}
        {instrumentFit && (
          <Row icon="🎯" label={instrumentFit.label} reason={instrumentFit.reason} color="#8b5cf6" />
        )}
        {entryStrategy && (
          <Row icon="⏱" label={entryStrategy.label} reason={entryStrategy.reason} color={entryStrategy.color} />
        )}
      </div>
    </div>
  );
}

function FOExpiriesCollapsible({ foExpiries, colors }) {
  const [expanded, setExpanded] = useState(false);
  if (!foExpiries?.length) return null;

  const fmtExpiry = iso => {
    const d = new Date(iso + 'T00:00:00');
    return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  };

  const tag = dte => {
    if (dte <= 7)  return { label: 'W', color: '#f59e0b' };
    if (dte <= 35) return { label: 'M', color: '#8b5cf6' };
    return               { label: 'Q', color: '#0ea5e9' };
  };

  return (
    <div
      className="rounded-lg px-3 py-2"
      style={{ background: 'linear-gradient(135deg, #8b5cf612 0%, #8b5cf604 100%)', border: '1px solid #8b5cf620' }}
    >
      <div className="flex items-center justify-between mb-2">
        <p style={{ fontSize: 8, fontWeight: 700, letterSpacing: '0.07em', color: '#9CA3AF', textTransform: 'uppercase' }}>
          FO Expiries · ATM {foExpiries[0]?.strike}
        </p>
        <button
          onClick={() => setExpanded(v => !v)}
          className="flex items-center gap-0.5"
          style={{ fontSize: 9, color: '#8b5cf6' }}
        >
          {expanded ? 'Hide' : 'Expand'}
          {expanded
            ? <ChevronUp style={{ width: 10, height: 10 }} />
            : <ChevronDown style={{ width: 10, height: 10 }} />}
        </button>
      </div>

      {/* summary row — always visible */}
      <div className="flex gap-1 flex-wrap">
        {foExpiries.map(exp => {
          const t = tag(exp.dte);
          const straddle = exp.straddle?.close;
          return (
            <div
              key={exp.expiry}
              className="flex items-center gap-1 px-1.5 py-0.5 rounded"
              style={{ background: `${t.color}12`, border: `1px solid ${t.color}25` }}
            >
              <span style={{ fontSize: 8, fontWeight: 700, color: t.color }}>{t.label}</span>
              <span style={{ fontSize: 9, color: colors.primaryText }}>{fmtExpiry(exp.expiry)}</span>
              {straddle && (
                <span style={{ fontSize: 9, color: '#9CA3AF' }}>₹{straddle}</span>
              )}
            </div>
          );
        })}
      </div>

      {expanded && (
        <div className="mt-2 flex flex-col gap-1">
          {/* header */}
          <div className="flex" style={{ paddingBottom: 4, borderBottom: `1px solid ${colors.border}` }}>
            <span style={{ flex: '0 0 52px', fontSize: 8, color: '#6B7280', textTransform: 'uppercase' }}>Expiry</span>
            <span style={{ flex: '0 0 28px', fontSize: 8, color: '#6B7280', textTransform: 'uppercase' }}>DTE</span>
            <span style={{ flex: 1, fontSize: 8, color: '#16C784', textTransform: 'uppercase', textAlign: 'right' }}>CE ₹</span>
            <span style={{ flex: 1, fontSize: 8, color: '#ef4444', textTransform: 'uppercase', textAlign: 'right' }}>PE ₹</span>
            <span style={{ flex: 1, fontSize: 8, color: '#9CA3AF', textTransform: 'uppercase', textAlign: 'right' }}>Straddle</span>
            <span style={{ flex: '0 0 36px', fontSize: 8, color: '#9CA3AF', textTransform: 'uppercase', textAlign: 'right' }}>Δ%</span>
          </div>
          {foExpiries.map(exp => {
            const t   = tag(exp.dte);
            const pct = exp.straddle?.pct;
            const pctColor = pct == null ? '#9CA3AF' : pct >= 0 ? '#ef4444' : '#16C784';
            return (
              <div key={exp.expiry} className="flex items-center" style={{ paddingTop: 2 }}>
                <div style={{ flex: '0 0 52px', display: 'flex', alignItems: 'center', gap: 3 }}>
                  <span style={{ fontSize: 8, fontWeight: 700, color: t.color, background: `${t.color}18`, padding: '0px 3px', borderRadius: 3 }}>{t.label}</span>
                  <span style={{ fontSize: 9, color: colors.primaryText }}>{fmtExpiry(exp.expiry)}</span>
                </div>
                <span style={{ flex: '0 0 28px', fontSize: 9, color: '#9CA3AF' }}>{exp.dte}d</span>
                <span style={{ flex: 1, fontSize: 10, fontWeight: 600, color: '#16C784', textAlign: 'right' }}>
                  {exp.CE?.close ?? '—'}
                </span>
                <span style={{ flex: 1, fontSize: 10, fontWeight: 600, color: '#ef4444', textAlign: 'right' }}>
                  {exp.PE?.close ?? '—'}
                </span>
                <span style={{ flex: 1, fontSize: 10, fontWeight: 600, color: colors.primaryText, textAlign: 'right' }}>
                  {exp.straddle?.close ?? '—'}
                </span>
                <span style={{ flex: '0 0 36px', fontSize: 9, fontWeight: 600, color: pctColor, textAlign: 'right' }}>
                  {pct != null ? `${pct > 0 ? '+' : ''}${pct}%` : '—'}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ScannerCollapsible({ scanners, colors }) {
  const [expanded, setExpanded] = useState(false);
  const bull = scanners.bull || {};
  const bear = scanners.bear || {};

  return (
    <div
      className="rounded-lg px-3 py-2"
      style={{ background: 'linear-gradient(135deg, #0ea5e912 0%, #0ea5e904 100%)', border: '1px solid #0ea5e920' }}
    >
      <div className="flex items-center justify-between mb-2">
        <p style={{ fontSize: 8, fontWeight: 700, letterSpacing: '0.07em', color: '#9CA3AF', textTransform: 'uppercase' }}>
          Scanner Signals
        </p>
        <button
          onClick={() => setExpanded(v => !v)}
          className="flex items-center gap-0.5"
          style={{ fontSize: 9, color: ACCENT }}
        >
          {expanded ? 'Hide' : 'Stocks'}
          {expanded
            ? <ChevronUp style={{ width: 10, height: 10 }} />
            : <ChevronDown style={{ width: 10, height: 10 }} />}
        </button>
      </div>

      <div className="flex gap-2 flex-wrap">
        {bull.signals > 0 && (
          <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 20, background: '#16C78418', color: '#16C784', border: '1px solid #16C78430' }}>
            {bull.signals} bullish · {bull.hitRate}% hit
          </span>
        )}
        {bear.signals > 0 && (
          <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 20, background: '#ef444418', color: '#ef4444', border: '1px solid #ef444430' }}>
            {bear.signals} bearish · {bear.hitRate}% hit
          </span>
        )}
      </div>

      {expanded && (
        <div className="mt-2 flex flex-col gap-2">
          {bull.stocks?.length > 0 && (
            <div>
              <p style={{ fontSize: 8, color: '#16C784', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>Bullish</p>
              {bull.stocks.map((n, i) => <StockRow key={i} n={n} isBull={true} />)}
            </div>
          )}
          {bear.stocks?.length > 0 && (
            <div>
              <p style={{ fontSize: 8, color: '#ef4444', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>Bearish</p>
              {bear.stocks.map((n, i) => <StockRow key={i} n={n} isBull={false} />)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── DayCard ───────────────────────────────────────────────────────────────────

// Same gap/open-behavior meta text used by ACT's Section 2, so both pages
// describe these states identically.
const GAP_META = {
  strong_up:   { text: 'Strong Gap Up',   sub: 'Gap ≥ +1% — institutions drove overnight. Do not chase open.', color: '#16C784' },
  mild_up:     { text: 'Mild Gap Up',     sub: 'Gap +0.3–1% — mild positive bias. Watch first 15 min.',        color: '#16C784' },
  flat:        { text: 'Flat Open',       sub: 'Gap within ±0.3% — no overnight bias. Open behavior decides.', color: '#f59e0b' },
  mild_down:   { text: 'Mild Gap Down',   sub: 'Gap −0.3–1% — mild negative bias. Watch for stabilisation.',   color: '#EF4444' },
  strong_down: { text: 'Strong Gap Down', sub: 'Gap ≤ −1% — aggressive longs are high risk at open.',          color: '#EF4444' },
};
const OPEN_BEHAVIOR_META = {
  sustained: { text: 'Sustained',      sub: 'Gap held — price continued in gap direction. Trend day.',               color: '#16C784' },
  reversal:  { text: 'Sharp Reversal', sub: 'Price went opposite to expectation. Original bias was wrong.',           color: '#EF4444' },
  mixed:     { text: 'Mixed Signals',  sub: 'Global and FII cues conflicted. Watch price action for real direction.', color: '#f59e0b' },
  range:     { text: 'Range-bound',    sub: 'Flat open, no directional bias. Wait for breakout from morning range.', color: '#8b5cf6' },
  gap_fill:  { text: 'Gap Fill',       sub: 'Opened with gap but moved to fill it. Fading the gap is the play.',     color: '#f97316' },
};

function fmtIdxDate(iso) {
  if (!iso) return '—';
  const d = new Date(`${iso}T00:00:00`);
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}

function IndexTile({ idx, colors }) {
  const pos = idx.pct >= 0;
  const tint = idx.pct == null ? colors.secondaryText : pos ? '#16C784' : '#EF4444';
  return (
    <div className="rounded-xl p-2.5" style={{ background: `linear-gradient(135deg, ${tint}12 0%, ${tint}04 100%)`, border: `1px solid ${tint}25` }}>
      <p className="text-[11px] font-semibold truncate" style={{ color: colors.primaryText }}>{idx.name}</p>
      <p className="text-[9px] mb-1" style={{ color: colors.secondaryText }}>{fmtIdxDate(idx.price_date)}</p>
      <p className="text-[14px] font-bold tabular-nums" style={{ color: tint }}>
        {idx.pct == null ? '—' : `${pos ? '+' : ''}${idx.pct.toFixed(2)}%`}
      </p>
    </div>
  );
}

// Per-index breakdown grid — mirrors ACT's Section 1 Global Cues layout
// (US / Asia / Europe+Dollar groups of individual index tiles) instead of a
// single collapsed "Global" average tile.
function GlobalIndexGrid({ components, colors }) {
  if (!components || !Object.keys(components).length) return null;
  const usIdx   = components.us?.indices?.filter(i => i.symbol !== '^IXIC') || [];
  const nasdaq  = components.us?.indices?.filter(i => i.symbol === '^IXIC') || [];
  const asiaIdx = components.asia?.indices || [];
  const euIdx   = components.eu?.indices?.filter(i => i.symbol !== 'DX-Y.NYB') || [];
  const dxy     = components.eu?.indices?.filter(i => i.symbol === 'DX-Y.NYB') || [];

  return (
    <div className="flex flex-col gap-2">
      {(usIdx.length > 0 || nasdaq.length > 0) && (
        <div>
          <p style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.07em', color: 'rgba(255,255,255,0.25)', marginBottom: 6, textTransform: 'uppercase' }}>
            US — prev day close
          </p>
          <div className="grid grid-cols-3 gap-1.5">
            {[...usIdx, ...nasdaq].map(i => <IndexTile key={i.symbol} idx={i} colors={colors} />)}
          </div>
        </div>
      )}
      {asiaIdx.length > 0 && (
        <div>
          <p style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.07em', color: 'rgba(255,255,255,0.25)', marginBottom: 6, textTransform: 'uppercase' }}>
            Asia — morning signal
          </p>
          <div className="grid grid-cols-2 gap-1.5">
            {asiaIdx.map(i => <IndexTile key={i.symbol} idx={i} colors={colors} />)}
          </div>
        </div>
      )}
      {(euIdx.length > 0 || dxy.length > 0) && (
        <div>
          <p style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.07em', color: 'rgba(255,255,255,0.25)', marginBottom: 6, textTransform: 'uppercase' }}>
            Europe + Dollar
          </p>
          <div className="grid grid-cols-3 gap-1.5">
            {[...euIdx, ...dxy].map(i => <IndexTile key={i.symbol} idx={i} colors={colors} />)}
          </div>
        </div>
      )}
    </div>
  );
}

// Section wrapper matching ACT's Section 1/2/3 pattern — icon + label header,
// tinted card, so this page's Expected -> Actual -> Verdict story reads the
// same way ACT's scenario context does.
function RegimeSection({ number, label, icon, accent, colors, children }) {
  return (
    <div className="rounded-xl p-3" style={{ background: `${accent}0d`, border: `1px solid ${accent}25` }}>
      <div className="flex items-center gap-1.5 mb-2.5">
        {icon}
        <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.07em', color: accent, textTransform: 'uppercase' }}>
          Section {number} — {label}
        </span>
      </div>
      <div className="flex flex-col gap-2.5">
        {children}
      </div>
    </div>
  );
}

function DayCard({ day, colors, onShare }) {
  const [open, setOpen] = useState(false);
  const accentBorder = day.conflict ? '#f59e0b' : day.match ? '#16C784' : '#ef4444';

  return (
    <div
      className="rounded-xl overflow-hidden"
      style={{
        background: colors.card,
        border: `1px solid ${colors.border}`,
        borderLeft: `3px solid ${accentBorder}`,
        boxShadow: colors.cardShadow,
      }}
    >
      <button
        className="w-full px-3 py-3 flex items-center justify-between gap-2"
        onClick={() => setOpen(o => !o)}
      >
        <div className="flex flex-col items-start gap-1 min-w-0">
          <span className="text-[12px] font-semibold" style={{ color: colors.primaryText }}>
            {day.date}
          </span>
          <MatchBadge match={day.match} conflict={day.conflict} />
        </div>

        <div className="flex items-center gap-3 flex-shrink-0">
          <div className="flex items-center gap-1.5">
            <div className="flex items-center gap-0.5" title="Global">
              <Globe style={{ width: 9, height: 9, color: colors.secondaryText }} />
              <BiasIcon bias={day.globalBias} />
            </div>
            <div className="flex items-center gap-0.5" title="Gap">
              <BarChart2 style={{ width: 9, height: 9, color: colors.secondaryText }} />
              <BiasIcon bias={gapBias(day.gapType)} />
            </div>
            <div className="flex items-center gap-0.5" title="FII">
              <Users style={{ width: 9, height: 9, color: colors.secondaryText }} />
              <BiasIcon bias={day.fiiBias} />
            </div>
          </div>

          <span
            className="text-[13px] font-bold tabular-nums w-14 text-right"
            style={{ color: day.actual === 'bull' ? '#16C784' : day.actual === 'bear' ? '#ef4444' : colors.secondaryText }}
          >
            {day.actualLabel}
          </span>

          {onShare && (
            <button
              onClick={e => { e.stopPropagation(); onShare(day); }}
              className="p-1 rounded-full"
              style={{ background: 'rgba(99,102,241,0.12)', border: '1px solid rgba(99,102,241,0.25)' }}
              title="Share this day"
            >
              <Share2 style={{ width: 10, height: 10, color: '#818cf8' }} />
            </button>
          )}
          {open
            ? <ChevronUp  style={{ width: 14, height: 14, color: colors.secondaryText }} />
            : <ChevronDown style={{ width: 14, height: 14, color: colors.secondaryText }} />}
        </div>
      </button>

      {open && (
        <div className="px-3 pb-3 flex flex-col gap-3" style={{ borderTop: `1px solid ${colors.border}` }}>

          <div style={{ marginTop: 12 }}>
          <RegimeSection number={1} label="Global Cues" accent="#0ea5e9" colors={colors}
            icon={<Globe style={{ width: 11, height: 11, color: '#0ea5e9' }} />}>

          {/* ── Per-index breakdown (US / Asia / Europe+Dollar) ─────────── */}
          <GlobalIndexGrid components={day.globalComponents} colors={colors} />

          {/* ── 4 signal tiles ─────────────────────────────────────────── */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {[
              {
                key: 'global',
                icon: <Globe style={{ width: 8, height: 8, color: colors.secondaryText }} />,
                label: 'Global',
                bias: day.globalBias,
                val: day.globalValue,
                badge: <GlobalStory components={day.globalComponents} />,
              },
              {
                key: 'gap',
                icon: <BarChart2 style={{ width: 8, height: 8, color: colors.secondaryText }} />,
                label: 'Gap',
                bias: gapBias(day.gapType),
                val: day.gapValue,
                badge: <GapStory day={day} />,
              },
              {
                key: 'fii',
                icon: <Users style={{ width: 8, height: 8, color: colors.secondaryText }} />,
                label: 'FII',
                bias: day.fiiBias,
                val: day.fiiValue,
                badge: <FlowStory day={day} which="fii" />,
              },
              {
                key: 'dii',
                icon: <Users style={{ width: 8, height: 8, color: colors.secondaryText }} />,
                label: 'DII',
                bias: day.diiBias,
                val: day.diiValue,
                badge: <FlowStory day={day} which="dii" />,
              },
            ].map(({ key, icon, label, bias, val, badge }) => (
              <div
                key={key}
                className="rounded-lg px-2.5 py-2.5"
                style={{
                  background: `linear-gradient(135deg, ${biasColor(bias)}18 0%, ${biasColor(bias)}06 100%)`,
                  border: `1px solid ${biasColor(bias)}28`,
                }}
              >
                <div className="flex items-center justify-between" style={{ marginBottom: 7, minHeight: 14 }}>
                  <div className="flex items-center gap-1">
                    {icon}
                    <span style={{ fontSize: 9, color: colors.secondaryText, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</span>
                  </div>
                  {badge}
                </div>
                <BiasIcon bias={bias} size={13} />
                {val && (
                  <p style={{ fontSize: 9, color: biasColor(bias), fontWeight: 600, marginTop: 4, lineHeight: 1.3 }}>{val}</p>
                )}
              </div>
            ))}
          </div>

          {/* ── Verdict ───────────────────────────────────────────────── */}
          <VerdictRow verdict={day.verdictSentence} colors={colors} />

          </RegimeSection>
          </div>

          <RegimeSection number={2} label="Nifty Regime" accent="#f59e0b" colors={colors}
            icon={<Activity style={{ width: 11, height: 11, color: '#f59e0b' }} />}>

          {/* ── Alert strip — expiry / conflict / high VIX ──────────────── */}
          {(day.isExpiry || day.conflict || (day.vixClose != null && day.vixClose > 20)) && (
            <div className="flex flex-wrap gap-1.5">
              {day.isExpiry && (
                <span style={{ fontSize: 10, fontWeight: 700, padding: '4px 10px', borderRadius: 999, background: '#f9731620', color: '#f97316', border: '1px solid #f9731640' }}>
                  Expiry Day — premium risk
                </span>
              )}
              {day.conflict && (
                <span style={{ fontSize: 10, fontWeight: 700, padding: '4px 10px', borderRadius: 999, background: '#ef444420', color: '#ef4444', border: '1px solid #ef444440' }}>
                  Conflict — choppy day
                </span>
              )}
              {day.vixClose != null && day.vixClose > 20 && (
                <span style={{ fontSize: 10, fontWeight: 700, padding: '4px 10px', borderRadius: 999, background: '#f59e0b20', color: '#f59e0b', border: '1px solid #f59e0b40' }}>
                  VIX {day.vixClose.toFixed(1)} — elevated volatility
                </span>
              )}
            </div>
          )}

          {/* ── Gap at Open + Open Behavior ──────────────────────────────── */}
          {day.gapType && (
            <div className="grid grid-cols-2 gap-2">
              <div className="rounded-lg px-3 py-2.5" style={{ background: colors.inputBg || '#ffffff08' }}>
                <p style={{ fontSize: 10, color: colors.secondaryText }}>Nifty Gap at Open</p>
                <p style={{ fontSize: 14, fontWeight: 700, marginTop: 2, color: GAP_META[day.gapType]?.color }}>
                  {day.gapVal != null ? `${day.gapVal >= 0 ? '+' : ''}${day.gapVal.toFixed(2)}%` : '—'}
                </p>
                <p style={{ fontSize: 10, marginTop: 2, color: GAP_META[day.gapType]?.color }}>
                  {GAP_META[day.gapType]?.text}
                </p>
                <p style={{ fontSize: 10, marginTop: 4, lineHeight: 1.4, color: colors.secondaryText }}>
                  {GAP_META[day.gapType]?.sub}
                </p>
              </div>
              <div className="rounded-lg px-3 py-2.5" style={{ background: colors.inputBg || '#ffffff08' }}>
                <p style={{ fontSize: 10, color: colors.secondaryText }}>Nifty Open Behavior</p>
                <p style={{ fontSize: 14, fontWeight: 700, marginTop: 2, color: OPEN_BEHAVIOR_META[day.openBehavior]?.color }}>
                  {OPEN_BEHAVIOR_META[day.openBehavior]?.text || day.openBehavior || '—'}
                </p>
                <p style={{ fontSize: 10, marginTop: 4, lineHeight: 1.4, color: colors.secondaryText }}>
                  {OPEN_BEHAVIOR_META[day.openBehavior]?.sub}
                </p>
              </div>
            </div>
          )}

          {/* ── Expected -> Actual + lock time ──────────────────────────── */}
          <div
            className="rounded-lg px-3.5 py-3 flex flex-col gap-2"
            style={{ background: 'linear-gradient(135deg, #0ea5e918 0%, #0ea5e908 100%)', border: '1px solid #0ea5e925' }}
          >
            <div className="flex items-center gap-2 flex-wrap">
              <span style={{ fontSize: 9, color: '#6B7280' }}>Expected</span>
              <BiasIcon bias={day.expected === 'conflict' ? 'neutral' : day.expected} size={12} />
              <span style={{ fontSize: 9, color: '#6B7280' }}>→ Actual</span>
              <BiasIcon bias={day.actual} size={12} />
              <span style={{ fontSize: 11, fontWeight: 700, color: day.actual === 'bull' ? '#16C784' : day.actual === 'bear' ? '#ef4444' : '#9CA3AF' }}>
                {day.actualLabel}
              </span>
              <MatchBadge match={day.match} conflict={day.conflict} />
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <span style={{ fontSize: 9, color: ACCENT, background: `${ACCENT}15`, padding: '3px 8px', borderRadius: 6 }}>
                {day.dayType}
              </span>
              {day.lockTime && (
                <div className="flex items-center gap-1">
                  <Clock style={{ width: 9, height: 9, color: '#6B7280' }} />
                  <span style={{ fontSize: 9, color: '#6B7280' }}>
                    Locked <span style={{ color: colors.primaryText, fontWeight: 600 }}>{day.lockTime}</span>
                  </span>
                </div>
              )}
            </div>
          </div>

          </RegimeSection>

          <RegimeSection number={3} label="F&O & Structure" accent="#8b5cf6" colors={colors}
            icon={<BarChart2 style={{ width: 11, height: 11, color: '#8b5cf6' }} />}>

          {/* ── Market Structure ───────────────────────────────────────── */}
          {(day.breadth != null || day.fo) && (
            <div
              className="rounded-lg px-3.5 py-3"
              style={{ background: 'linear-gradient(135deg, #8b5cf618 0%, #8b5cf608 100%)', border: '1px solid #8b5cf625' }}
            >
              <div className="flex items-center gap-1.5 mb-2.5">
                <p style={{ fontSize: 8, fontWeight: 700, letterSpacing: '0.07em', color: '#8b5cf6', textTransform: 'uppercase' }}>
                  Market Structure
                </p>
                <InfoCard items={MARKET_STRUCTURE_INFO} />
              </div>

              {day.breadth != null && (
                <div className="flex items-center gap-2 mb-2">
                  <span style={{ fontSize: 9, color: '#6B7280', minWidth: 48 }}>Breadth</span>
                  <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: colors.border }}>
                    <div
                      className="h-full rounded-full"
                      style={{
                        width: `${day.breadth}%`,
                        background: day.breadth >= 60 ? '#16C784' : day.breadth <= 40 ? '#ef4444' : '#f59e0b',
                      }}
                    />
                  </div>
                  <span style={{
                    fontSize: 9, fontWeight: 600, minWidth: 72, textAlign: 'right',
                    color: day.breadth >= 60 ? '#16C784' : day.breadth <= 40 ? '#ef4444' : '#f59e0b',
                  }}>
                    {day.breadth}% · {day.breadthCount}
                  </span>
                </div>
              )}

              {day.fo && (
                <div className="flex items-center gap-2 flex-wrap">
                  <span style={{ fontSize: 9, color: '#6B7280' }}>ATM {day.fo.strike}</span>
                  <div className="w-px h-3" style={{ background: '#ffffff20' }} />
                  {['CE', 'PE', 'straddle'].map((key, idx) => {
                    const d = day.fo[key];
                    if (!d) return null;
                    const lbl = key === 'CE' ? 'Call' : key === 'PE' ? 'Put' : 'Straddle';
                    const c = key === 'straddle'
                      ? (d.pct > 0 ? '#f59e0b' : '#9CA3AF')
                      : (d.pct > 0 ? '#16C784' : '#ef4444');
                    return (
                      <React.Fragment key={key}>
                        <div className="flex items-center gap-1">
                          <span style={{ fontSize: 9, color: '#6B7280' }}>{lbl}</span>
                          <span style={{ fontSize: 10, fontWeight: 700, color: c }}>
                            {d.pct >= 0 ? '+' : ''}{d.pct}%
                          </span>
                        </div>
                        {idx < 2 && <div className="w-px h-3" style={{ background: '#ffffff15' }} />}
                      </React.Fragment>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ── FO Performance ────────────────────────────────────────── */}
          <FOPerformanceSection foPerformance={day.foPerformance} colors={colors} />

          </RegimeSection>

          {/* ── Sectors ───────────────────────────────────────────────── */}
          {SHOW_SECONDARY_ANALYSIS && day.sectors?.length > 0 && (
            <div
              className="rounded-lg px-3 py-2"
              style={{ background: 'linear-gradient(135deg, #f59e0b12 0%, transparent 100%)', border: '1px solid #f59e0b20' }}
            >
              <p style={{ fontSize: 8, fontWeight: 700, letterSpacing: '0.07em', color: '#9CA3AF', marginBottom: 5, textTransform: 'uppercase' }}>
                Sectors
              </p>
              <div className="flex flex-wrap gap-1">
                {day.sectors.map(sec => {
                  const c = sec.avg >= 0 ? '#16C784' : '#ef4444';
                  return (
                    <div
                      key={sec.name}
                      className="flex items-center gap-1 px-1.5 py-0.5 rounded-full"
                      style={{ background: `${c}12`, border: `1px solid ${c}25` }}
                    >
                      <span style={{ fontSize: 9, color: '#E5E7EB' }}>{sec.name}</span>
                      <span style={{ fontSize: 9, fontWeight: 700, color: c }}>{sec.avg >= 0 ? '+' : ''}{sec.avg}%</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Scanner ───────────────────────────────────────────────── */}
          {SHOW_SECONDARY_ANALYSIS && day.scanners && (day.scanners.bull?.signals > 0 || day.scanners.bear?.signals > 0) && (
            <ScannerCollapsible scanners={day.scanners} colors={colors} />
          )}

          {/* ── What Worked ───────────────────────────────────────────── */}
          {SHOW_SECONDARY_ANALYSIS && <WhatWorkedSection day={day} colors={colors} />}

          {day.note && (
            <p style={{ fontSize: 11, lineHeight: 1.5, color: colors.secondaryText }}>{day.note}</p>
          )}
        </div>
      )}
    </div>
  );
}

// ── SignalReliability ─────────────────────────────────────────────────────────

function SignalReliability({ combos, colors }) {
  if (!combos.length) return null;
  return (
    <div className="flex flex-col gap-2">
      {combos.map(({ regime, hitRate, days, color }) => (
        <div
          key={regime}
          className="rounded-xl px-3 py-2.5 flex items-center gap-3"
          style={{
            background: colors.card,
            border: `1px solid ${colors.border}`,
            boxShadow: colors.cardShadow,
          }}
        >
          <div className="flex-1 min-w-0">
            <p className="text-[11px] font-medium leading-tight" style={{ color: colors.primaryText }}>
              {regime}
            </p>
            <p className="text-[10px] mt-0.5" style={{ color: colors.secondaryText }}>
              {days} day{days !== 1 ? 's' : ''}
            </p>
          </div>
          <div className="flex flex-col items-end gap-1 flex-shrink-0">
            <span className="text-[15px] font-bold tabular-nums" style={{ color }}>
              {hitRate}%
            </span>
            <div className="h-1.5 rounded-full overflow-hidden" style={{ width: 56, background: colors.border }}>
              <div className="h-full rounded-full" style={{ width: `${hitRate}%`, background: color }} />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Loading / Error states ────────────────────────────────────────────────────

function LoadingState({ colors }) {
  return (
    <div className="flex flex-col gap-3 py-8 items-center">
      <RefreshCw style={{ width: 20, height: 20, color: ACCENT }} className="animate-spin" />
      <p className="text-[12px]" style={{ color: colors.secondaryText }}>
        Computing regime analysis…
      </p>
    </div>
  );
}

function ErrorState({ error, onRetry, colors }) {
  return (
    <div
      className="rounded-xl px-4 py-6 flex flex-col items-center gap-3"
      style={{ background: colors.card, border: `1px solid ${colors.border}` }}
    >
      <AlertTriangle style={{ width: 20, height: 20, color: '#ef4444' }} />
      <p className="text-[12px] text-center" style={{ color: colors.secondaryText }}>
        {error || 'Failed to load analysis data'}
      </p>
      <button
        onClick={onRetry}
        className="text-[11px] px-3 py-1.5 rounded-lg font-semibold"
        style={{ background: `${ACCENT}20`, color: ACCENT }}
      >
        Retry
      </button>
    </div>
  );
}

// ── Simulator ─────────────────────────────────────────────────────────────────

const NIFTY50_STOCKS = [
  { symbol: 'ADANIENT.NS',   name: 'Adani Ent' },
  { symbol: 'ADANIPORTS.NS', name: 'Adani Ports' },
  { symbol: 'APOLLOHOSP.NS', name: 'Apollo Hospital' },
  { symbol: 'ASIANPAINT.NS', name: 'Asian Paints' },
  { symbol: 'AXISBANK.NS',   name: 'Axis Bank' },
  { symbol: 'BAJAJ-AUTO.NS', name: 'Bajaj Auto' },
  { symbol: 'BAJAJFINSV.NS', name: 'Bajaj Finserv' },
  { symbol: 'BAJFINANCE.NS', name: 'Bajaj Finance' },
  { symbol: 'BHARTIARTL.NS', name: 'Bharti Airtel' },
  { symbol: 'BPCL.NS',       name: 'BPCL' },
  { symbol: 'BRITANNIA.NS',  name: 'Britannia' },
  { symbol: 'CIPLA.NS',      name: 'Cipla' },
  { symbol: 'COALINDIA.NS',  name: 'Coal India' },
  { symbol: 'DIVISLAB.NS',   name: "Divi's Lab" },
  { symbol: 'DRREDDY.NS',    name: "Dr Reddy's" },
  { symbol: 'EICHERMOT.NS',  name: 'Eicher Motors' },
  { symbol: 'ETERNAL.NS',    name: 'Eternal' },
  { symbol: 'GRASIM.NS',     name: 'Grasim' },
  { symbol: 'HCLTECH.NS',    name: 'HCL Tech' },
  { symbol: 'HDFCBANK.NS',   name: 'HDFC Bank' },
  { symbol: 'HDFCLIFE.NS',   name: 'HDFC Life' },
  { symbol: 'HEROMOTOCO.NS', name: 'Hero MotoCorp' },
  { symbol: 'HINDALCO.NS',   name: 'Hindalco' },
  { symbol: 'HINDUNILVR.NS', name: 'HUL' },
  { symbol: 'ICICIBANK.NS',  name: 'ICICI Bank' },
  { symbol: 'INDUSINDBK.NS', name: 'IndusInd Bank' },
  { symbol: 'INFY.NS',       name: 'Infosys' },
  { symbol: 'ITC.NS',        name: 'ITC' },
  { symbol: 'JSWSTEEL.NS',   name: 'JSW Steel' },
  { symbol: 'KOTAKBANK.NS',  name: 'Kotak Bank' },
  { symbol: 'LT.NS',         name: 'L&T' },
  { symbol: 'M&M.NS',        name: 'M&M' },
  { symbol: 'MARUTI.NS',     name: 'Maruti' },
  { symbol: 'NESTLEIND.NS',  name: 'Nestle India' },
  { symbol: 'NTPC.NS',       name: 'NTPC' },
  { symbol: 'ONGC.NS',       name: 'ONGC' },
  { symbol: 'POWERGRID.NS',  name: 'Power Grid' },
  { symbol: 'RELIANCE.NS',   name: 'Reliance' },
  { symbol: 'SBILIFE.NS',    name: 'SBI Life' },
  { symbol: 'SBIN.NS',       name: 'SBI' },
  { symbol: 'SHRIRAMFIN.NS', name: 'Shriram Finance' },
  { symbol: 'SUNPHARMA.NS',  name: 'Sun Pharma' },
  { symbol: 'TATACONSUM.NS', name: 'Tata Consumer' },
  { symbol: 'TATASTEEL.NS',  name: 'Tata Steel' },
  { symbol: 'TCS.NS',        name: 'TCS' },
  { symbol: 'TECHM.NS',      name: 'Tech Mahindra' },
  { symbol: 'TITAN.NS',      name: 'Titan' },
  { symbol: 'TRENT.NS',      name: 'Trent' },
  { symbol: 'ULTRACEMCO.NS', name: 'UltraTech Cement' },
  { symbol: 'WIPRO.NS',      name: 'Wipro' },
];

const ENTRY_TIMES = ['09:15','09:20','09:35','09:50','10:05','10:20','10:35','11:00','11:30','12:00','13:00','14:00','14:30'];

function SimulatorTab({ days, colors, cardStyle }) {
  const [selDate,    setSelDate]    = useState(() => [...days].reverse()[0]?.date_iso || null);
  const [tradeType,  setTradeType]  = useState('fo');
  const [foInst,     setFoInst]     = useState('CE');
  const [strike,     setStrike]     = useState('');
  const [expiry,     setExpiry]     = useState('');
  const [direction,  setDirection]  = useState('buy');
  const [holding,    setHolding]    = useState('intraday');
  const [stockQ,     setStockQ]     = useState('');
  const [selStock,   setSelStock]   = useState(null);
  const [entryTime,  setEntryTime]  = useState('09:35');
  const [sl,         setSl]         = useState('');
  const [target,     setTarget]     = useState('');
  const [simLoading,    setSimLoading]    = useState(false);
  const [result,        setResult]        = useState(null);
  const [simError,      setSimError]      = useState(null);
  const [simSocialOpen, setSimSocialOpen] = useState(false);
  const [simCapturing,  setSimCapturing]  = useState(false);
  const simCardRef = useRef(null);
  const [strikeList,    setStrikeList]    = useState([]);
  const [strikesLoading,setStrikesLoading]= useState(false);
  const [equityOpen,    setEquityOpen]    = useState(null);
  const [equityLoading, setEquityLoading] = useState(false);

  const day = days.find(d => d.date_iso === selDate);

  useEffect(() => {
    if (day?.fo?.strike) setStrike(String(day.fo.strike));
    const firstExp = day?.foExpiries?.[0]?.expiry;
    if (firstExp) setExpiry(firstExp);
    setResult(null); setSimError(null);
  }, [selDate]);

  useEffect(() => { setResult(null); setSimError(null); }, [tradeType, foInst, selStock, direction, holding]);

  // Fetch F&O strikes for selected date + instrument
  useEffect(() => {
    if (!selDate || tradeType !== 'fo' || foInst === 'FUT') { setStrikeList([]); return; }
    setStrikesLoading(true);
    axios.get(`/api/analytics/trade-sim/fo-strikes?date=${selDate}&instrument=${foInst}`)
      .then(res => {
        const list = res.data.strikes || [];
        setStrikeList(list);
        // keep ATM strike selected if available
        const atm = day?.fo?.strike;
        if (atm && list.some(s => s.strike === atm)) setStrike(String(atm));
        else if (list.length) setStrike(String(list[Math.floor(list.length / 2)].strike));
      })
      .catch(() => setStrikeList([]))
      .finally(() => setStrikesLoading(false));
  }, [selDate, foInst, tradeType]);

  // Fetch equity open price when stock + date are set
  useEffect(() => {
    if (!selDate || !selStock || tradeType !== 'equity') { setEquityOpen(null); return; }
    setEquityLoading(true);
    axios.get(`/api/analytics/trade-sim/equity-price?date=${selDate}&symbol=${selStock.symbol}`)
      .then(res => setEquityOpen(res.data.openPrice))
      .catch(() => setEquityOpen(null))
      .finally(() => setEquityLoading(false));
  }, [selDate, selStock, tradeType]);

  const filteredStocks = NIFTY50_STOCKS.filter(s =>
    !stockQ ||
    s.name.toLowerCase().includes(stockQ.toLowerCase()) ||
    s.symbol.replace('.NS', '').toLowerCase().includes(stockQ.toLowerCase())
  );

  const selectedStrikeData = strikeList.find(s => String(s.strike) === String(strike));
  const entryHint = tradeType === 'fo'
    ? foInst === 'FUT' ? day?.niftyOpen
    : selectedStrikeData?.open || null
    : equityOpen;

  const canRun    = selDate && sl && target && (tradeType === 'fo' || selStock);
  const slHint    = entryHint ? Math.round(direction === 'buy' ? entryHint * 0.85 : entryHint * 1.15) : null;
  const targetHint= entryHint ? Math.round(direction === 'buy' ? entryHint * 1.20 : entryHint * 0.80) : null;

  async function runSim() {
    setSimLoading(true); setSimError(null); setResult(null);
    try {
      if (tradeType === 'equity') {
        const p = new URLSearchParams({ date: selDate, symbol: selStock.symbol, direction, sl, target, holding });
        const res = await axios.get(`/api/analytics/trade-sim/equity?${p}`);
        setResult({ ...res.data, _mode: 'equity' });
      } else {
        const p = new URLSearchParams({
          date: selDate, instrument: foInst, entry_time: entryTime, sl, target, direction, holding,
          ...(foInst !== 'FUT' && strike && { strike }),
          ...(foInst !== 'FUT' && expiry  && { expiry }),
        });
        const res = await axios.get(`/api/analytics/trade-sim?${p}`);
        setResult({ ...res.data, _mode: 'fo' });
      }
    } catch (e) {
      setSimError(e?.response?.data?.detail || 'Simulation failed');
    } finally {
      setSimLoading(false);
    }
  }

  const fmtDate = iso => {
    if (!iso) return '—';
    const d = new Date(iso + 'T00:00:00');
    return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  };

  const outcomeColor = !result ? '#9CA3AF'
    : result.outcome === 'TARGET_HIT' ? '#16C784'
    : result.outcome === 'SL_HIT'     ? '#ef4444'
    : '#f59e0b';

  const outcomeLabel = !result ? ''
    : result.outcome === 'TARGET_HIT' ? '✅ Target Hit'
    : result.outcome === 'SL_HIT'     ? '❌ SL Hit'
    : result.outcome === 'AMBIGUOUS'  ? '⚠️ Ambiguous'
    : result.outcome === 'NO_DATA'    ? '⚠️ No Data'
    : '⏱ Held to Close';

  const lbl = { fontSize: 9, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 7 };
  const activeBtn   = (bg) => ({ background: bg || ACCENT, color: '#fff' });
  const inactiveBtn = () => ({ background: colors.card, color: colors.secondaryText, border: `1px solid ${colors.border}` });

  return (
    <div className="flex flex-col gap-3">

      {/* Step 1 — Date */}
      <div className="rounded-xl px-3 py-3" style={cardStyle}>
        <p style={lbl}>Select Date</p>
        <div className="flex flex-wrap gap-1.5">
          {[...days].reverse().map(d => (
            <button
              key={d.date_iso}
              onClick={() => setSelDate(d.date_iso)}
              className="px-2.5 py-1 rounded-lg text-[11px] font-semibold transition-all"
              style={selDate === d.date_iso ? activeBtn() : inactiveBtn()}
            >
              {d.date}
            </button>
          ))}
        </div>
        {day && (
          <p style={{ fontSize: 10, color: '#6B7280', marginTop: 6 }}>
            <span style={{ color: day.globalBias === 'bull' ? '#16C784' : day.globalBias === 'bear' ? '#ef4444' : '#9CA3AF', fontWeight: 600 }}>
              {day.globalBias ? day.globalBias.charAt(0).toUpperCase() + day.globalBias.slice(1) : '—'}
            </span>
            {' global · Nifty '}
            <span style={{ color: day.niftyChangePct > 0 ? '#16C784' : '#ef4444', fontWeight: 600 }}>
              {day.niftyChangePct > 0 ? '+' : ''}{day.niftyChangePct}%
            </span>
            {day.fo?.strike ? ` · ATM ${day.fo.strike}` : ''}
          </p>
        )}
      </div>

      {selDate && (
        <>
          {/* Step 2 — Trade Type + Direction + Holding */}
          <div className="rounded-xl px-3 py-3 flex flex-col gap-3" style={cardStyle}>

            <div>
              <p style={lbl}>Market</p>
              <div className="flex gap-2">
                {[{ k: 'fo', l: 'F&O  (Nifty)' }, { k: 'equity', l: 'Equity  (Nifty 50)' }].map(({ k, l }) => (
                  <button key={k} onClick={() => { setTradeType(k); setResult(null); }}
                    className="flex-1 py-1.5 rounded-xl text-[12px] font-semibold transition-all"
                    style={tradeType === k ? activeBtn() : inactiveBtn()}
                  >{l}</button>
                ))}
              </div>
            </div>

            <div className="flex gap-3">
              <div className="flex-1">
                <p style={lbl}>Direction</p>
                <div className="flex gap-2">
                  {[{ k: 'buy', l: '▲ Buy', c: '#16C784' }, { k: 'sell', l: '▼ Sell', c: '#ef4444' }].map(({ k, l, c }) => (
                    <button key={k} onClick={() => setDirection(k)}
                      className="flex-1 py-1.5 rounded-xl text-[12px] font-semibold transition-all"
                      style={direction === k ? { background: c, color: '#fff' } : inactiveBtn()}
                    >{l}</button>
                  ))}
                </div>
              </div>
              <div className="flex-1">
                <p style={lbl}>Holding</p>
                <div className="flex gap-2">
                  {[{ k: 'intraday', l: 'Intraday' }, { k: 'carry', l: 'Carry' }].map(({ k, l }) => (
                    <button key={k} onClick={() => setHolding(k)}
                      className="flex-1 py-1.5 rounded-xl text-[12px] font-semibold transition-all"
                      style={holding === k ? activeBtn() : inactiveBtn()}
                    >{l}</button>
                  ))}
                </div>
              </div>
            </div>

            {holding === 'carry' && (
              <p style={{ fontSize: 10, color: '#f59e0b' }}>
                Carry: scans from selected day up to sim date — system won't look past what it knows
              </p>
            )}
          </div>

          {/* Step 3a — F&O instrument */}
          {tradeType === 'fo' && (
            <div className="rounded-xl px-3 py-3" style={cardStyle}>
              <p style={lbl}>Instrument</p>
              <div className="flex gap-2 flex-wrap">
                {[
                  ...(day?.fo?.CE ? [{ k: 'CE',  l: 'CE' }] : []),
                  ...(day?.fo?.PE ? [{ k: 'PE',  l: 'PE' }] : []),
                  { k: 'FUT', l: 'Futures' },
                ].map(({ k, l }) => (
                  <button key={k} onClick={() => setFoInst(k)}
                    className="px-4 py-1.5 rounded-lg text-[11px] font-semibold transition-all"
                    style={foInst === k ? activeBtn() : inactiveBtn()}
                  >{l}</button>
                ))}
              </div>
              {foInst !== 'FUT' && (() => {
                const atm = day?.fo?.strike;
                // Show strikes within ±1000 of ATM (±20 strikes at 50pt intervals)
                const visible = atm
                  ? strikeList.filter(s => Math.abs(s.strike - atm) <= 1000)
                  : strikeList;
                return (
                  <div className="mt-3">
                    <p style={{ ...lbl, marginBottom: 4 }}>Strike Price</p>
                    {strikesLoading && <p style={{ fontSize: 10, color: '#6B7280' }}>Loading strikes…</p>}
                    {!strikesLoading && visible.length > 0 && (
                      <div className="flex flex-wrap gap-1.5" style={{ maxHeight: 120, overflowY: 'auto' }}>
                        {visible.map(s => {
                          const isAtm  = atm && s.strike === atm;
                          const isSel  = String(s.strike) === String(strike);
                          return (
                            <button
                              key={s.strike}
                              onClick={() => setStrike(String(s.strike))}
                              className="px-2.5 py-1 rounded-lg text-[11px] font-semibold transition-all"
                              style={isSel
                                ? activeBtn()
                                : isAtm
                                ? { background: `${ACCENT}20`, color: ACCENT, border: `1px solid ${ACCENT}50` }
                                : inactiveBtn()
                              }
                            >
                              {s.strike}{isAtm ? ' ★' : ''}
                            </button>
                          );
                        })}
                      </div>
                    )}
                    {!strikesLoading && visible.length === 0 && (
                      <p style={{ fontSize: 10, color: '#6B7280' }}>No strike data for this date</p>
                    )}
                    {selectedStrikeData && (
                      <p style={{ fontSize: 11, fontWeight: 600, color: ACCENT, marginTop: 6 }}>
                        Open at 9:15 → ₹{selectedStrikeData.open}
                      </p>
                    )}
                    {day?.foExpiries?.length > 0 && (
                      <div className="mt-3">
                        <p style={{ ...lbl, marginBottom: 4 }}>Expiry</p>
                        <div className="flex flex-wrap gap-1.5">
                          {day.foExpiries.map(fe => (
                            <button
                              key={fe.expiry}
                              onClick={() => setExpiry(fe.expiry)}
                              className="px-3 py-1 rounded-lg text-[11px] font-semibold transition-all"
                              style={expiry === fe.expiry ? activeBtn() : inactiveBtn()}
                            >
                              {fe.expiry}{fe.tag ? ` · ${fe.tag}` : ''}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          )}

          {/* Step 3b — Equity stock */}
          {tradeType === 'equity' && (
            <div className="rounded-xl px-3 py-3" style={cardStyle}>
              <p style={lbl}>Select Stock</p>
              <input
                type="text" value={stockQ} onChange={e => setStockQ(e.target.value)}
                placeholder="Search…"
                className="w-full px-3 py-2 rounded-lg text-[12px] mb-2"
                style={{ background: colors.background, border: `1px solid ${colors.border}`, color: colors.primaryText, outline: 'none' }}
              />
              <div className="flex flex-wrap gap-1.5" style={{ maxHeight: 130, overflowY: 'auto' }}>
                {filteredStocks.map(s => (
                  <button
                    key={s.symbol}
                    onClick={() => { setSelStock(s); setStockQ(''); }}
                    className="px-2.5 py-1 rounded-lg text-[11px] font-semibold transition-all"
                    style={selStock?.symbol === s.symbol ? activeBtn() : inactiveBtn()}
                  >
                    {s.name}
                  </button>
                ))}
              </div>
              {selStock && (
                <div className="flex flex-col gap-2 mt-3">
                  <p style={{ fontSize: 11, color: ACCENT, fontWeight: 600 }}>
                    {selStock.name}
                    <span style={{ color: '#6B7280', fontWeight: 400, fontSize: 10 }}> · {selStock.symbol.replace('.NS', '')}</span>
                  </p>
                  {equityLoading && <p style={{ fontSize: 10, color: '#6B7280' }}>Fetching price…</p>}
                  {!equityLoading && equityOpen != null && (
                    <div className="rounded-lg px-3 py-2" style={{ background: `${ACCENT}10`, border: `1px solid ${ACCENT}25` }}>
                      <p style={{ fontSize: 9, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 2 }}>Open Price on {day?.date}</p>
                      <p style={{ fontSize: 18, fontWeight: 800, color: ACCENT }}>₹{equityOpen.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                      <p style={{ fontSize: 9, color: '#6B7280', marginTop: 2 }}>
                        {direction === 'buy'
                          ? 'Set SL below this price · Target above'
                          : 'Set SL above this price · Target below'}
                      </p>
                    </div>
                  )}
                  {!equityLoading && equityOpen == null && (
                    <p style={{ fontSize: 10, color: '#f59e0b' }}>No price data for {selStock.name} on {day?.date}</p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Step 4 — Entry time (FO intraday) + SL/Target */}
          <div className="rounded-xl px-3 py-3" style={cardStyle}>
            {tradeType === 'fo' && holding === 'intraday' && (
              <div className="mb-3">
                <p style={lbl}>Entry Time (IST)</p>
                <div className="flex gap-1.5 flex-wrap">
                  {ENTRY_TIMES.map(t => (
                    <button key={t} onClick={() => setEntryTime(t)}
                      className="px-2.5 py-1 rounded-lg text-[10px] font-semibold transition-all"
                      style={entryTime === t ? activeBtn() : inactiveBtn()}
                    >{t}</button>
                  ))}
                </div>
                {entryHint != null && (
                  <p style={{ fontSize: 9, color: '#6B7280', marginTop: 4 }}>
                    Open at 9:15 was <span style={{ color: colors.primaryText, fontWeight: 600 }}>₹{entryHint}</span> — actual entry at {entryTime} may differ
                  </p>
                )}
              </div>
            )}
            {(tradeType === 'equity' || (tradeType === 'fo' && holding === 'carry')) && (
              <p style={{ fontSize: 10, color: '#6B7280', marginBottom: 10 }}>
                {tradeType === 'fo' ? 'Carry: entry at 9:15 open price' : 'Entry at market open'} ·{' '}
                {direction === 'buy' ? 'SL below entry · Target above' : 'SL above entry · Target below'}
              </p>
            )}
            <div className="flex gap-3">
              <div className="flex-1">
                <p style={{ ...lbl, marginBottom: 4 }}>
                  Stop Loss ₹
                  <span style={{ color: '#6B7280', fontWeight: 400 }}> ({direction === 'buy' ? 'below entry' : 'above entry'})</span>
                </p>
                <input
                  type="number" value={sl} onChange={e => setSl(e.target.value)}
                  placeholder={slHint ? `e.g. ${slHint}` : '0'}
                  className="w-full px-3 py-2 rounded-lg text-[12px]"
                  style={{ background: colors.background, border: `1px solid ${colors.border}`, color: colors.primaryText, outline: 'none' }}
                />
              </div>
              <div className="flex-1">
                <p style={{ ...lbl, marginBottom: 4 }}>
                  Target ₹
                  <span style={{ color: '#6B7280', fontWeight: 400 }}> ({direction === 'buy' ? 'above entry' : 'below entry'})</span>
                </p>
                <input
                  type="number" value={target} onChange={e => setTarget(e.target.value)}
                  placeholder={targetHint ? `e.g. ${targetHint}` : '0'}
                  className="w-full px-3 py-2 rounded-lg text-[12px]"
                  style={{ background: colors.background, border: `1px solid ${colors.border}`, color: colors.primaryText, outline: 'none' }}
                />
              </div>
            </div>
          </div>

          {/* Run */}
          <button
            onClick={runSim}
            disabled={simLoading || !canRun}
            className="w-full py-3 rounded-xl text-[13px] font-bold transition-all"
            style={{ background: !canRun ? '#1e293b' : ACCENT, color: !canRun ? '#6B7280' : '#fff' }}
          >
            {simLoading ? 'Simulating…' : 'Run Simulation'}
          </button>

          {simError && <p style={{ fontSize: 11, color: '#ef4444', textAlign: 'center' }}>{simError}</p>}

          {/* Result */}
          {result && result.outcome !== 'NO_DATA' && (
            <div className="rounded-xl p-4 flex flex-col gap-3" style={{ background: `${outcomeColor}10`, border: `1px solid ${outcomeColor}30` }}>

              {/* Outcome + Net P&L */}
              <div className="flex items-center justify-between">
                <div>
                  <p style={{ fontSize: 15, fontWeight: 800, color: outcomeColor }}>{outcomeLabel}</p>
                  {result.holding === 'carry' && result.exitDate && (
                    <p style={{ fontSize: 11, color: outcomeColor, fontWeight: 600, marginTop: 2 }}>
                      Fulfilled on {fmtDate(result.exitDate)}
                      {result.daysHeld > 1 ? ` · ${result.daysHeld} days held` : ' · same day'}
                    </p>
                  )}
                </div>
                <div style={{ textAlign: 'right' }}>
                  <p style={{ fontSize: 22, fontWeight: 800, color: outcomeColor }}>
                    {result._mode === 'fo'
                      ? `${result.pnl > 0 ? '+' : result.pnl < 0 ? '-' : ''}₹${Math.abs(result.pnl).toLocaleString('en-IN')}`
                      : `${result.pnl > 0 ? '+' : result.pnl < 0 ? '-' : ''}₹${Math.abs(result.pnl).toFixed(2)}`
                    }
                    {result._mode === 'equity' && (
                      <span style={{ fontSize: 10, color: '#6B7280', fontWeight: 400 }}> /share</span>
                    )}
                  </p>
                  {result._mode === 'fo' && result.commission > 0 && (
                    <p style={{ fontSize: 9, color: '#6B7280', marginTop: 1 }}>
                      gross ₹{result.grossPnl >= 0 ? '+' : ''}{result.grossPnl} − ₹{result.commission} commission
                    </p>
                  )}
                </div>
              </div>

              {/* Details grid */}
              <div className="grid grid-cols-2 gap-2">
                {(result._mode === 'fo' && result.holding === 'intraday' ? [
                  { label: 'Entry',    value: `${result.entryTime}  ·  ₹${result.entryPrice}` },
                  { label: 'Exit',     value: `${result.exitTime}  ·  ₹${result.exitPrice}`   },
                  { label: 'SL',       value: `₹${result.sl}` },
                  { label: 'Target',   value: `₹${result.target}` },
                  { label: 'Day High', value: `₹${result.maxPrice}` },
                  { label: 'Day Low',  value: `₹${result.minPrice}` },
                ] : result._mode === 'fo' && result.holding === 'carry' ? [
                  { label: 'Entry',      value: `₹${result.entryPrice}  (9:15 open)` },
                  { label: 'Exit',       value: `₹${result.exitPrice}` },
                  { label: 'SL',         value: `₹${result.sl}` },
                  { label: 'Target',     value: `₹${result.target}` },
                  { label: 'Exit Date',  value: fmtDate(result.exitDate) },
                  { label: 'Days Held',  value: `${result.daysHeld} day${result.daysHeld !== 1 ? 's' : ''}` },
                ] : result._mode === 'equity' && result.holding === 'carry' ? [
                  { label: 'Entry',      value: `₹${result.entryPrice}  (open)` },
                  { label: 'Exit',       value: `₹${result.exitPrice}` },
                  { label: 'SL',         value: `₹${result.sl}` },
                  { label: 'Target',     value: `₹${result.target}` },
                  { label: 'Exit Date',  value: fmtDate(result.exitDate) },
                  { label: 'Days Held',  value: `${result.daysHeld} day${result.daysHeld !== 1 ? 's' : ''}` },
                  { label: 'Day High',   value: `₹${result.dayHigh}` },
                  { label: 'Day Low',    value: `₹${result.dayLow}` },
                ] : [
                  { label: 'Entry',    value: `₹${result.entryPrice}  (open)` },
                  { label: 'Exit',     value: `₹${result.exitPrice}` },
                  { label: 'SL',       value: `₹${result.sl}` },
                  { label: 'Target',   value: `₹${result.target}` },
                  { label: 'Day High', value: `₹${result.dayHigh}` },
                  { label: 'Day Low',  value: `₹${result.dayLow}` },
                ]).map(({ label, value }) => (
                  <div key={label} className="rounded-lg px-3 py-2" style={{ background: colors.card }}>
                    <p style={{ fontSize: 8, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 2 }}>{label}</p>
                    <p style={{ fontSize: 11, fontWeight: 600, color: colors.primaryText }}>{value}</p>
                  </div>
                ))}
              </div>

              {/* Footer notes */}
              {result._mode === 'fo' && result.candlesScanned && (
                <p style={{ fontSize: 9, color: '#6B7280' }}>{result.candlesScanned} 1-min candles scanned · 1 lot (25 qty)</p>
              )}
              {result._mode === 'equity' && result.outcome === 'AMBIGUOUS' && (
                <p style={{ fontSize: 9, color: '#f59e0b' }}>
                  Both SL and Target within the day's range — exact outcome depends on intraday sequence
                </p>
              )}
              {result._mode === 'equity' && result.holding === 'carry' && result.exitDate && (
                <p style={{ fontSize: 9, color: '#6B7280' }}>
                  Exit date: {fmtDate(result.exitDate)} · day {result.daysHeld} of {result.daysScanned} scanned · Daily OHLC
                </p>
              )}
              {result._mode === 'equity' && result.holding === 'intraday' && (
                <p style={{ fontSize: 9, color: '#6B7280' }}>Daily OHLC · intraday sequence not available for equity</p>
              )}
              {result._mode === 'equity' && (
                <p style={{ fontSize: 9, color: '#6B7280' }}>+~₹{result.commission} brokerage per order (not deducted — varies by qty)</p>
              )}
            </div>
          )}

          {/* Post as Image button */}
          {result && result.outcome !== 'NO_DATA' && (
            <button
              onClick={() => setSimSocialOpen(true)}
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-[13px] font-bold"
              style={{ background: 'rgba(99,102,241,0.12)', border: '1px solid rgba(99,102,241,0.3)', color: '#818cf8' }}>
              <Share2 className="w-4 h-4" /> Post as Image
            </button>
          )}

          {result?.outcome === 'NO_DATA' && (
            <p style={{ fontSize: 11, color: '#f59e0b', textAlign: 'center' }}>
              ⚠️ No data found for this instrument on {day?.date}
            </p>
          )}
        </>
      )}

      {/* Hidden card for html2canvas */}
      {result && (
        <div style={{ position: 'fixed', left: -9999, top: 0, opacity: 0, pointerEvents: 'none', zIndex: -1 }}>
          <SimulatorShareCard cardRef={simCardRef} snap={{
            result, day,
            instrument: tradeType === 'fo' ? foInst : (selStock?.name || ''),
            stockName: selStock?.name || '',
            direction, holding,
          }} />
        </div>
      )}

      {/* Social share bottom sheet */}
      {simSocialOpen && result && (
        <div className="fixed inset-0 z-50 flex items-end justify-center" style={{ backgroundColor: 'rgba(0,0,0,0.7)' }} onClick={() => setSimSocialOpen(false)}>
          <div className="w-full max-w-sm rounded-t-2xl p-5 space-y-4" style={{ background: colors.cardBg || '#141927', border: `1px solid ${colors.border}` }} onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <p className="text-[15px] font-bold" style={{ color: colors.primaryText }}>Share Simulation</p>
              <button onClick={() => setSimSocialOpen(false)}><X className="w-4 h-4" style={{ color: colors.secondaryText }} /></button>
            </div>
            <div style={{ width: '100%', height: Math.round(1350 * (300/1080)), overflow: 'hidden', borderRadius: 10 }}>
              <div style={{ width: 1080, height: 1350, transform: `scale(${300/1080})`, transformOrigin: 'top left' }}>
                <SimulatorShareCard snap={{ result, day, instrument: tradeType === 'fo' ? foInst : (selStock?.name || ''), stockName: selStock?.name || '', direction, holding }} />
              </div>
            </div>
            <p className="text-[12px] text-center" style={{ color: colors.secondaryText }}>Opens share sheet — pick Instagram, WhatsApp, or save to camera roll.</p>
            <button
              onClick={async () => {
                if (!simCardRef.current) return;
                setSimCapturing(true);
                try { await captureAndShare(simCardRef.current, 'simulation.png'); }
                catch (e) { console.error('Share failed', e); }
                finally { setSimCapturing(false); setSimSocialOpen(false); }
              }}
              disabled={simCapturing}
              className="w-full py-3 rounded-xl text-[14px] font-semibold flex items-center justify-center gap-2"
              style={{ background: '#0ea5e9', color: '#fff', opacity: simCapturing ? 0.6 : 1 }}>
              {simCapturing ? <><Loader className="w-4 h-4 animate-spin" /> Preparing…</> : <><Share2 className="w-4 h-4" /> Share as Image</>}
            </button>
          </div>
        </div>
      )}

    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

// ─── Regime hero carousel ────────────────────────────────────────────────────
const HERO_SLIDES = [
  {
    color: '#10b981',
    icon: CheckCircle2,
    label: 'What it is',
    title: 'A daily signal scorecard.',
    body: 'Was pre-market bias confirmed by price action? Alignment = signal worked. Conflict = market reversed.',
  },
  {
    color: '#f59e0b',
    icon: AlertTriangle,
    label: 'Why you need it',
    title: 'Not all days trade the same.',
    body: 'High-alignment days have better hit rates. Conflict days are coin-flip territory — reduce size or sit out.',
  },
  {
    color: ACCENT,
    icon: Target,
    label: 'How to use it',
    title: 'Check before you size up.',
    body: 'High hit rate + best entry window = trade full size. Conflict days = cut size or skip the session.',
  },
];
const HERO_DURATION = 5000;

function RegimeHeroBand() {
  const [active, setActive] = React.useState(0);
  const [dragOffset, setDragOffset] = React.useState(0);
  const isDragging = React.useRef(false);
  const touchStartX = React.useRef(null);
  const timerRef = React.useRef(null);

  React.useEffect(() => {
    if (!document.getElementById('regime-hero-kf')) {
      const s = document.createElement('style');
      s.id = 'regime-hero-kf';
      s.textContent = `@keyframes regimeProg { from { width:0% } to { width:100% } }`;
      document.head.appendChild(s);
    }
  }, []);

  const startTimer = React.useCallback(() => {
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => setActive(i => (i + 1) % HERO_SLIDES.length), HERO_DURATION);
  }, []);

  React.useEffect(() => { startTimer(); return () => clearInterval(timerRef.current); }, [startTimer]);

  const goTo = React.useCallback((i) => { setActive(i); startTimer(); }, [startTimer]);

  const onTouchStart = React.useCallback((e) => {
    touchStartX.current = e.touches[0].clientX;
    isDragging.current = true;
  }, []);

  const onTouchMove = React.useCallback((e) => {
    if (!touchStartX.current) return;
    setDragOffset(Math.max(-90, Math.min(90, e.touches[0].clientX - touchStartX.current)));
  }, []);

  const onTouchEnd = React.useCallback((e) => {
    if (!touchStartX.current) return;
    const diff = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    isDragging.current = false;
    setDragOffset(0);
    if (Math.abs(diff) > 40) {
      setActive(i => diff < 0 ? (i + 1) % HERO_SLIDES.length : (i - 1 + HERO_SLIDES.length) % HERO_SLIDES.length);
      startTimer();
    }
  }, [startTimer]);

  const slide = HERO_SLIDES[active];

  return (
    <div
      style={{
        borderRadius: 16, overflow: 'hidden', position: 'relative',
        border: `1px solid ${slide.color}30`,
        background: `linear-gradient(135deg, ${slide.color}13 0%, ${slide.color}05 100%)`,
        transition: 'border-color 0.5s ease, background 0.5s ease',
        userSelect: 'none',
      }}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      {/* Slides track */}
      <div style={{
        display: 'flex',
        transform: `translateX(calc(${-active * 100}% + ${dragOffset}px))`,
        transition: isDragging.current ? 'none' : 'transform 0.42s cubic-bezier(0.22,1,0.36,1)',
        willChange: 'transform',
      }}>
        {HERO_SLIDES.map((s, i) => {
          const Icon = s.icon;
          return (
            <div key={i} style={{ minWidth: '100%', padding: '18px 18px 12px', boxSizing: 'border-box', position: 'relative', overflow: 'hidden' }}>
              {/* Watermark icon */}
              <Icon size={90} color={s.color} style={{
                position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)',
                opacity: 0.07, pointerEvents: 'none',
              }} />
              <p style={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.12em', color: s.color, marginBottom: 6 }}>
                {s.label}
              </p>
              <p style={{ fontSize: 17, fontWeight: 800, color: '#eceef5', letterSpacing: '-0.02em', lineHeight: 1.2, marginBottom: 7 }}>
                {s.title}
              </p>
              <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.44)', lineHeight: 1.58 }}>
                {s.body}
              </p>
            </div>
          );
        })}
      </div>

      {/* Tappable progress segments */}
      <div style={{ display: 'flex', gap: 3, padding: '0 18px 13px' }}>
        {HERO_SLIDES.map((_, i) => (
          <button key={i} onClick={() => goTo(i)}
            style={{ flex: 1, height: 2, padding: 0, border: 'none', cursor: 'pointer', borderRadius: 1, background: `${slide.color}20`, overflow: 'hidden' }}>
            {i < active && <div style={{ height: '100%', width: '100%', background: slide.color, opacity: 0.5 }} />}
            {i === active && (
              <div key={`rp-${active}`} style={{ height: '100%', background: slide.color, animation: `regimeProg ${HERO_DURATION}ms linear forwards` }} />
            )}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function MarketAnalysisPage() {
  const { colors } = useTheme();
  const navigate   = useNavigate();
  const [period,    setPeriod]    = useState('1w');
  const [activeTab, setActiveTab] = useState('simulator');
  const [data,      setData]      = useState(null);
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState(null);
  const [regimeSocialOpen, setRegimeSocialOpen] = useState(false);
  const [regimeCapturing,  setRegimeCapturing]  = useState(false);
  const regimeCardRef = useRef(null);
  const [dayShareOpen,    setDayShareOpen]    = useState(false);
  const [dayShareDay,     setDayShareDay]     = useState(null);
  const [dayCapturing,    setDayCapturing]    = useState(false);
  const dayCardRef = useRef(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`/api/analytics/market-analysis?period=${period}`);
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load analysis data');
    } finally {
      setLoading(false);
    }
  }, [period]);


  useEffect(() => { fetchData(); }, [fetchData]);

  const cardStyle = {
    background: colors.card,
    border: `1px solid ${colors.border}`,
    boxShadow: colors.cardShadow,
  };

  return (
    <div className="flex flex-col gap-4 pb-4">

      {/* Header */}
      <div className="flex items-center gap-3">
        <div
          className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ background: `${ACCENT}20` }}
        >
          <Activity style={{ width: 16, height: 16, color: ACCENT }} />
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="text-[16px] font-bold leading-tight" style={{ color: colors.primaryText }}>
            Market Regime Analysis
          </h1>
          <p className="text-[11px]" style={{ color: colors.secondaryText }}>
            Did the market follow its pre-market signals?
          </p>
        </div>
        {data && activeTab === 'regime' && (
          <button
            onClick={() => setRegimeSocialOpen(true)}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[11px] font-bold flex-shrink-0"
            style={{ background: 'rgba(99,102,241,0.12)', color: '#818cf8', border: '1px solid rgba(99,102,241,0.3)' }}>
            <Share2 style={{ width: 12, height: 12 }} /> Post
          </button>
        )}
      </div>

      {/* ── Hero carousel ── */}
      <div data-tour="regime-hero"><RegimeHeroBand /></div>

      {/* Tab + Period selector row */}
      <div data-tour="regime-tabs" className="flex items-center justify-between gap-2">
        {/* Tabs */}
        <div className="flex gap-1 p-1 rounded-xl" style={{ background: colors.card, border: `1px solid ${colors.border}` }}>
          {[{ key: 'simulator', label: 'Simulator' }, { key: 'regime', label: 'Regime' }].map(t => (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              className="px-4 py-1.5 rounded-lg text-[12px] font-semibold transition-all"
              style={activeTab === t.key
                ? { background: ACCENT, color: '#fff' }
                : { color: colors.secondaryText }
              }
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Period selector */}
        <div className="flex gap-1.5">
          {PERIODS.map(p => (
            <button
              key={p.key}
              onClick={() => setPeriod(p.key)}
              className="px-3 py-1.5 rounded-lg text-[12px] font-semibold transition-all"
              style={period === p.key
                ? { background: `${ACCENT}25`, color: ACCENT, border: `1px solid ${ACCENT}50` }
                : { background: colors.card, color: colors.secondaryText, border: `1px solid ${colors.border}` }
              }
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Loading */}
      {loading && <LoadingState colors={colors} />}

      {/* Error */}
      {!loading && error && <ErrorState error={error} onRetry={fetchData} colors={colors} />}


      {/* Simulator tab */}
      {!loading && !error && data && activeTab === 'simulator' && (
        <div data-tour="regime-simulator"><SimulatorTab days={data.days} colors={colors} cardStyle={cardStyle} /></div>
      )}

      {/* Regime tab content */}
      {!loading && !error && data && activeTab === 'regime' && (() => {
        const { summary, actionSummary, signalCombos, days, windows } = data;
        const periodLabel = period === '1w' ? 'week' : period === '1m' ? 'month' : '3 months';
        const maxHitRate  = Math.max(...windows.map(w => w.hitRate), 1);

        return (
          <>
            {/* Summary cards */}
            <div data-tour="regime-summary-cards" className="grid grid-cols-2 gap-2">
              {[
                { label: 'Alignment Hit Rate', value: `${summary.hitRate}%`, sub: `${summary.daysAnalyzed} days analysed`, icon: Target,       color: '#16C784' },
                { label: 'Conflict Days',      value: summary.conflictDays,   sub: 'signal contradictions',               icon: AlertTriangle, color: '#f59e0b' },
                { label: 'Best Entry Window',  value: summary.bestWindow,     sub: 'highest hit rate',                    icon: Clock,         color: ACCENT   },
                { label: 'Days Analysed',      value: summary.daysAnalyzed,   sub: `last ${periodLabel}`,                 icon: BarChart2,     color: '#8b5cf6' },
              ].map(({ label, value, sub, icon: Icon, color }) => (
                <div key={label} className="rounded-xl px-3 py-3" style={cardStyle}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[10px] uppercase tracking-wider" style={{ color: colors.secondaryText }}>
                      {label}
                    </span>
                    <Icon style={{ width: 12, height: 12, color }} />
                  </div>
                  <div className="text-[20px] font-bold leading-tight" style={{ color }}>{value}</div>
                  <div className="text-[10px] mt-0.5" style={{ color: colors.secondaryText }}>{sub}</div>
                </div>
              ))}
            </div>

            {/* Signal Reliability */}
            {SHOW_SECONDARY_ANALYSIS && signalCombos.length > 0 && (
              <div data-tour="regime-signal-reliability">
                <div className="flex items-center gap-2 mb-2">
                  <Zap style={{ width: 11, height: 11, color: ACCENT }} />
                  <span className="text-[11px] font-semibold uppercase tracking-widest whitespace-nowrap" style={{ color: ACCENT }}>
                    Signal Reliability
                  </span>
                  <div className="flex-1 h-px" style={{ backgroundColor: colors.border }} />
                </div>
                <SignalReliability combos={signalCombos} colors={colors} />
              </div>
            )}

            {/* Day-by-day */}
            <div data-tour="regime-day-cards"><ActionSummaryCard actionSummary={actionSummary} colors={colors} /></div>

            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-[11px] font-semibold uppercase tracking-widest whitespace-nowrap" style={{ color: ACCENT }}>
                  Day-by-Day Regime
                </span>
                <div className="flex-1 h-px" style={{ backgroundColor: colors.border }} />
                {period !== '1w' && days.length < summary.daysAnalyzed && (
                  <span className="text-[10px] flex-shrink-0" style={{ color: colors.secondaryText }}>
                    recent {days.length} of {summary.daysAnalyzed}
                  </span>
                )}
              </div>
              {days.length === 0 ? (
                <p className="text-[12px] text-center py-4" style={{ color: colors.secondaryText }}>
                  No trading days found for this period
                </p>
              ) : (
                <div className="flex flex-col gap-2">
                  {days.map(day => (
                    <DayCard key={day.date_iso || day.date} day={day} colors={colors}
                      onShare={(d) => { setDayShareDay(d); setDayShareOpen(true); }} />
                  ))}
                </div>
              )}
            </div>

            {/* Best entry window */}
            {SHOW_SECONDARY_ANALYSIS && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-[11px] font-semibold uppercase tracking-widest whitespace-nowrap" style={{ color: ACCENT }}>
                  Best Entry Window
                </span>
                <div className="flex-1 h-px" style={{ backgroundColor: colors.border }} />
              </div>
              <div className="rounded-xl px-3 py-3" style={cardStyle}>
                <p className="text-[10px] mb-3" style={{ color: colors.secondaryText }}>
                  % of trending days where entering at this time led to a profitable close
                </p>
                <div className="flex flex-col gap-2">
                  {windows.map(w => {
                    const isBest = w.hitRate === maxHitRate && w.hitRate > 0;
                    return (
                      <div key={w.label} className="flex items-center gap-2">
                        <span
                          className="text-[11px] tabular-nums w-10 text-right flex-shrink-0"
                          style={{ color: isBest ? ACCENT : colors.secondaryText, fontWeight: isBest ? '700' : '400' }}
                        >
                          {w.label}
                        </span>
                        <div className="flex-1 h-5 rounded-md overflow-hidden" style={{ background: colors.border }}>
                          {w.hitRate > 0 && (
                            <div
                              className="h-full rounded-md flex items-center pl-2"
                              style={{
                                width: `${w.hitRate}%`,
                                background: isBest
                                  ? `linear-gradient(90deg, ${ACCENT}, ${ACCENT}bb)`
                                  : `${ACCENT}35`,
                              }}
                            >
                              <span className="text-[10px] font-semibold" style={{ color: isBest ? '#fff' : ACCENT }}>
                                {w.hitRate}%
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <p
                  className="text-[10px] mt-3 pt-2"
                  style={{ color: colors.secondaryText, borderTop: `1px solid ${colors.border}` }}
                >
                  Based on trending days in last 30 days · 1-min Nifty Futures
                </p>
              </div>
            </div>
            )}
          </>
        );
      })()}

      {/* Hidden regime card for html2canvas */}
      {data && (
        <div style={{ position: 'fixed', left: -9999, top: 0, opacity: 0, pointerEvents: 'none', zIndex: -1 }}>
          <MarketRegimeShareCard cardRef={regimeCardRef} snap={{
            summary: data.summary,
            signalCombos: data.signalCombos || [],
            period,
            periodLabel: period === '1w' ? 'Last Week' : period === '1m' ? 'Last Month' : 'Last 3 Months',
          }} />
        </div>
      )}

      {/* Hidden per-day card for html2canvas */}
      {dayShareDay && (
        <div style={{ position: 'fixed', left: -9999, top: 0, opacity: 0, pointerEvents: 'none', zIndex: -1 }}>
          <RegimeDayShareCard cardRef={dayCardRef} day={dayShareDay} />
        </div>
      )}

      {/* Per-day share bottom sheet */}
      {dayShareOpen && dayShareDay && (
        <div className="fixed inset-0 z-50 flex items-end justify-center" style={{ backgroundColor: 'rgba(0,0,0,0.7)' }} onClick={() => setDayShareOpen(false)}>
          <div className="w-full max-w-sm rounded-t-2xl p-5 space-y-4" style={{ background: colors.cardBg || '#141927', border: `1px solid ${colors.border}` }} onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <p className="text-[15px] font-bold" style={{ color: colors.primaryText }}>Share Day Analysis · {dayShareDay.date}</p>
              <button onClick={() => setDayShareOpen(false)}><X style={{ width: 16, height: 16 }} color={colors.secondaryText} /></button>
            </div>
            <div style={{ width: '100%', height: Math.round(1350 * (300/1080)), overflow: 'hidden', borderRadius: 10 }}>
              <div style={{ width: 1080, height: 1350, transform: `scale(${300/1080})`, transformOrigin: 'top left' }}>
                <RegimeDayShareCard day={dayShareDay} />
              </div>
            </div>
            <p className="text-[12px] text-center" style={{ color: colors.secondaryText }}>Opens share sheet — pick Instagram, WhatsApp, or save to camera roll.</p>
            <button
              onClick={async () => {
                if (!dayCardRef.current) return;
                setDayCapturing(true);
                try { await captureAndShare(dayCardRef.current, `regime-day-${dayShareDay.date_iso || dayShareDay.date}.png`); }
                catch (e) { console.error('Day share failed', e); }
                finally { setDayCapturing(false); setDayShareOpen(false); }
              }}
              disabled={dayCapturing}
              className="w-full py-3 rounded-xl text-[14px] font-semibold flex items-center justify-center gap-2"
              style={{ background: '#0ea5e9', color: '#fff', opacity: dayCapturing ? 0.6 : 1 }}>
              {dayCapturing ? <><Loader style={{ width: 16, height: 16 }} className="animate-spin" /> Preparing…</> : <><Share2 style={{ width: 16, height: 16 }} /> Share as Image</>}
            </button>
          </div>
        </div>
      )}

      {/* Regime social share bottom sheet */}
      {regimeSocialOpen && data && (
        <div className="fixed inset-0 z-50 flex items-end justify-center" style={{ backgroundColor: 'rgba(0,0,0,0.7)' }} onClick={() => setRegimeSocialOpen(false)}>
          <div className="w-full max-w-sm rounded-t-2xl p-5 space-y-4" style={{ background: colors.cardBg || '#141927', border: `1px solid ${colors.border}` }} onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <p className="text-[15px] font-bold" style={{ color: colors.primaryText }}>Share Regime Analysis</p>
              <button onClick={() => setRegimeSocialOpen(false)}><X style={{ width: 16, height: 16 }} color={colors.secondaryText} /></button>
            </div>
            <div style={{ width: '100%', height: Math.round(1350 * (300/1080)), overflow: 'hidden', borderRadius: 10 }}>
              <div style={{ width: 1080, height: 1350, transform: `scale(${300/1080})`, transformOrigin: 'top left' }}>
                <MarketRegimeShareCard snap={{
                  summary: data.summary,
                  signalCombos: data.signalCombos || [],
                  period,
                  periodLabel: period === '1w' ? 'Last Week' : period === '1m' ? 'Last Month' : 'Last 3 Months',
                }} />
              </div>
            </div>
            <p className="text-[12px] text-center" style={{ color: colors.secondaryText }}>Opens share sheet — pick Instagram, WhatsApp, or save to camera roll.</p>
            <button
              onClick={async () => {
                if (!regimeCardRef.current) return;
                setRegimeCapturing(true);
                try { await captureAndShare(regimeCardRef.current, `regime-${period}.png`); }
                catch (e) { console.error('Share failed', e); }
                finally { setRegimeCapturing(false); setRegimeSocialOpen(false); }
              }}
              disabled={regimeCapturing}
              className="w-full py-3 rounded-xl text-[14px] font-semibold flex items-center justify-center gap-2"
              style={{ background: '#0ea5e9', color: '#fff', opacity: regimeCapturing ? 0.6 : 1 }}>
              {regimeCapturing ? <><Loader style={{ width: 16, height: 16 }} className="animate-spin" /> Preparing…</> : <><Share2 style={{ width: 16, height: 16 }} /> Share as Image</>}
            </button>
          </div>
        </div>
      )}

    </div>
  );
}
